import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Modal,
  Fade,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import Menu from '@mui/material/Menu';
import ChatIcon from '@mui/icons-material/Chat';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FolderIcon from '@mui/icons-material/Folder';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BarChartIcon from '@mui/icons-material/BarChart';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import PolicyIcon from '@mui/icons-material/Policy';
import CloseIcon from '@mui/icons-material/Close';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useMFA } from '../contexts/MFAContext';
import Security2FAWarning from '../components/Security2FAWarning';
import { usePermissions } from '../hooks/usePermissions';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { AiLlmTypes, ConversationTypes, PolicyTypes } from '@flashbacktech/flashbackclient/api';
import OrganizationUpgradePrompt from '../components/OrganizationUpgradePrompt';
import PrivateChatTutorial from '../components/PrivateChatTutorial';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Import AI provider logos
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import awsLogo from '../assets/cloud-providers/aws.svg';

// Provider icons reused for dynamic models
const AI_PROVIDER_ICONS: Record<AiLlmTypes.AiType, React.ReactNode> = {
  [AiLlmTypes.AiType.OPENAI]: (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="#10A37F">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
    </svg>
  ),
  [AiLlmTypes.AiType.GOOGLE]: (
    <img src={googleCloudLogo} alt="Google" style={{ width: '16px', height: '16px' }} />
  ),
  [AiLlmTypes.AiType.ANTHROPIC]: (
    <img src={anthropicLogo} alt="Anthropic" style={{ width: '16px', height: '16px' }} />
  ),
  [AiLlmTypes.AiType.AWS]: (
    <img src={awsLogo} alt="AWS" style={{ width: '16px', height: '16px' }} />
  ),
  [AiLlmTypes.AiType.OTHER]: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
};

// Provider display names
const AI_PROVIDER_NAMES: Record<AiLlmTypes.AiType, string> = {
  [AiLlmTypes.AiType.OPENAI]: 'OpenAI',
  [AiLlmTypes.AiType.GOOGLE]: 'Google',
  [AiLlmTypes.AiType.ANTHROPIC]: 'Anthropic',
  [AiLlmTypes.AiType.AWS]: 'AWS',
  [AiLlmTypes.AiType.OTHER]: 'Other',
};

// Providers array derived from enum + metadata
const AI_PROVIDERS = Object.values(AiLlmTypes.AiType).map((aiType) => ({
  id: aiType as AiLlmTypes.AiType,
  name: AI_PROVIDER_NAMES[aiType as AiLlmTypes.AiType],
  icon: AI_PROVIDER_ICONS[aiType as AiLlmTypes.AiType],
}));

// Flatten all models for compatibility (will be populated dynamically)
type UiModel = AiLlmTypes.AiModelDTO & {
  provider: string;
  providerIcon: React.ReactNode | null;
};

// Top 5 most commonly used models (shown directly in selector), by model ID
const TOP_MODELS = [
  'gpt-4o',
  'claude-3-opus',
  'gpt-4-turbo',
  'gemini-pro',
  'claude-3-sonnet',
];

// Model descriptions for display
const MODEL_DESCRIPTIONS = {
  'low-cost': 'Low-cost for short answers',
  'fast': 'Fast for basic questions',
  'efficient': 'Efficient for most of the cases.',
  'best': 'Best for research and suggestions',
};

// Featured models per provider (4 models each) with descriptions
// These are the pre-selected models shown in the dropdown before "More..."
const FEATURED_MODELS: Record<AiLlmTypes.AiType, { id: string; name: string; description: string }[]> = {
  [AiLlmTypes.AiType.OPENAI]: [
    { id: 'gpt-5-nano', name: 'GPT-5 nano', description: MODEL_DESCRIPTIONS['low-cost'] },
    { id: 'gpt-5-mini', name: 'GPT-5 mini', description: MODEL_DESCRIPTIONS['fast'] },
    { id: 'gpt-5.2', name: 'GPT-5.2', description: MODEL_DESCRIPTIONS['efficient'] },
    { id: 'gpt-5.2-pro', name: 'GPT-5.2 Pro', description: MODEL_DESCRIPTIONS['best'] },
  ],
  [AiLlmTypes.AiType.ANTHROPIC]: [
    { id: 'claude-3-haiku-20240307', name: 'Claude 3.0 Haiku', description: MODEL_DESCRIPTIONS['low-cost'] },
    { id: 'claude-haiku-4-5-20251001', name: 'Claude Haiku 4.5', description: MODEL_DESCRIPTIONS['fast'] },
    { id: 'claude-sonnet-4-5-20250929', name: 'Claude Sonnet 4.5', description: MODEL_DESCRIPTIONS['efficient'] },
    { id: 'claude-opus-4-5-20251101', name: 'Claude Opus 4.5', description: MODEL_DESCRIPTIONS['best'] },
  ],
  [AiLlmTypes.AiType.GOOGLE]: [
    { id: 'gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash-Lite', description: MODEL_DESCRIPTIONS['low-cost'] },
    { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: MODEL_DESCRIPTIONS['fast'] },
    { id: 'gemini-3-flash-preview', name: 'Gemini 3.0 Flash', description: MODEL_DESCRIPTIONS['efficient'] },
    { id: 'gemini-3-pro-preview', name: 'Gemini 3.0 Pro', description: MODEL_DESCRIPTIONS['best'] },
  ],
  [AiLlmTypes.AiType.AWS]: [
    { id: 'amazon.titan-text-lite-v1', name: 'Nova Lite', description: MODEL_DESCRIPTIONS['low-cost'] },
    { id: 'amazon.titan-text-express-v1', name: 'Nova Micro', description: MODEL_DESCRIPTIONS['fast'] },
    { id: 'mistral.mistral-7b-instruct-v0:2', name: 'Ministral 3 14B', description: MODEL_DESCRIPTIONS['efficient'] },
    { id: 'amazon.titan-text-premier-v1:0', name: 'Nova Pro', description: MODEL_DESCRIPTIONS['best'] },
  ],
  [AiLlmTypes.AiType.OTHER]: [],
};

// Response mode filters
const RESPONSE_MODES = [
  { id: 'short', label: 'Short', description: 'Concise answers' },
  { id: 'detailed', label: 'Detailed', description: 'Comprehensive responses' },
  { id: 'advanced', label: 'Advanced', description: 'Technical depth' },
];

// Get provider icon from AiType
const getProviderIcon = (aiType: AiLlmTypes.AiType) => AI_PROVIDER_ICONS[aiType] || null;

// Map AI type from repo to AiLlmTypes.AiType
const mapAiTypeToProviderId = (aiType: string): AiLlmTypes.AiType | null => {
  const typeUpper = (aiType || '').toUpperCase();
  switch (typeUpper) {
    case 'OPENAI':
      return AiLlmTypes.AiType.OPENAI;
    case 'ANTHROPIC':
      return AiLlmTypes.AiType.ANTHROPIC;
    case 'GOOGLE':
      return AiLlmTypes.AiType.GOOGLE;
    case 'AWS':
      return AiLlmTypes.AiType.AWS;
    default:
      return AiLlmTypes.AiType.OTHER;
  }
};

// Get provider icon from aiType (for displaying in environment selector)
const getProviderIconFromAiType = (aiType: string): JSX.Element | null => {
  const providerId = mapAiTypeToProviderId(aiType);
  if (!providerId) return null;
  return getProviderIcon(providerId) as JSX.Element | null;
};

// Get saved chat environment from localStorage
const getSavedChatEnvironment = (): { workspaceId: string; repoId: string } | null => {
  try {
    const saved = localStorage.getItem('chatEnvironment');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.workspaceId && parsed.repoId) {
        return { workspaceId: parsed.workspaceId, repoId: parsed.repoId };
      }
    }
  } catch { /* ignore */ }
  return null;
};

// AI LLM Stats interface (V2 shape from getAiGatewayBreakdown)
interface AILLMStats {
  aiLlmId: string;
  name: string;
  requests: number;
  tokensIn: number;
  tokensOut: number;
}

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, globalData } = useAuth();
  const { apiClient } = useApi();
  const permissions = usePermissions();
  const withTokenRefresh = useWithTokenRefresh();
  const { mfaState } = useMFA();
  const hasAnyMfaConfigured = (mfaState?.methods?.some((m) => m.isConfigured) ?? false);
  const is2FAEnabled = mfaState?.status?.isEnabled || hasAnyMfaConfigured;
  const [is2FABannerDismissed, setIs2FABannerDismissed] = useState(false);
  const handle2FABannerDismiss = () => {
    setIs2FABannerDismissed(true);
  };
  const [conversations, setConversations] = useState<ConversationTypes.ConversationDTO[]>([]);
  const [hasOrgConversations, setHasOrgConversations] = useState<boolean | null>(null); // null = not checked yet
  const [selectedConversation, setSelectedConversation] = useState<ConversationTypes.ConversationDTO | null>(null);
  const [messages, setMessages] = useState<ConversationTypes.MessageDTO[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedRepoId, setSelectedRepoId] = useState<string>(''); // For creating new conversation
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4');
  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;
  const [selectedResponseMode, setSelectedResponseMode] = useState<string>('detailed');
  const [availableModels, setAvailableModels] = useState<UiModel[]>([]);
  const [repos, setRepos] = useState<any[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const skipNextMessageLoadRef = useRef(false);
  const skipRepoResetRef = useRef(false); // Track when we're explicitly selecting a repo from another workspace
  
  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Chat Environment state - check localStorage for saved environment
  const savedEnv = getSavedChatEnvironment();
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(!savedEnv); // Only open if no saved environment
  const [environmentWorkspaceId, setEnvironmentWorkspaceId] = useState<string>(savedEnv?.workspaceId || '');
  const [showWorkspaceSelector, setShowWorkspaceSelector] = useState(false); // Hidden by default, user can expand
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [policiesView, setPoliciesView] = useState(false);
  const [metricsView, setMetricsView] = useState(false);
  const [selectedStatsPeriod, setSelectedStatsPeriod] = useState<string>('7d'); // Fixed to 7 days as per product requirements
  const [selectedTokenView, setSelectedTokenView] = useState<'in' | 'out' | 'total'>('total'); // Token view for Top 5 models
  const [chatMenuAnchor, setChatMenuAnchor] = useState<null | HTMLElement>(null);
  const [chatMenuConv, setChatMenuConv] = useState<ConversationTypes.ConversationDTO | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  
  // Model selector hover state (Cursor-like)
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [hoveredProvider, setHoveredProvider] = useState<AiLlmTypes.AiType | null>(AiLlmTypes.AiType.OPENAI);

  const modelSelectorRef = useRef<HTMLDivElement>(null);
  const modelSelectorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [renameValue, setRenameValue] = useState('');
  
  // Model search modal state
  const [modelSearchModalOpen, setModelSearchModalOpen] = useState(false);
  const [modelSearchQuery, setModelSearchQuery] = useState('');
  const [modelSearchProvider, setModelSearchProvider] = useState<AiLlmTypes.AiType | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [environmentRepoId, setEnvironmentRepoId] = useState<string>(savedEnv?.repoId || '');
  
  // New user warning modal state
  const [noResourcesModalOpen, setNoResourcesModalOpen] = useState(false);
  const [resourcesChecked, setResourcesChecked] = useState(false);
  const [reposLoaded, setReposLoaded] = useState(false);
  const [aiConfigsCount, setAiConfigsCount] = useState(0);
  const [aiConfigsLoaded, setAiConfigsLoaded] = useState(false);
  const [workspacesWithRepos, setWorkspacesWithRepos] = useState<any[]>([]);
  const [allReposGrouped, setAllReposGrouped] = useState<{workspace: any, repos: any[]}[]>([]);
  const [environmentSearchQuery, setEnvironmentSearchQuery] = useState('');
  const [suggestWorkspaceModalOpen, setSuggestWorkspaceModalOpen] = useState(false);
  const [showChatTutorial, setShowChatTutorial] = useState(false);

  // AI LLM Stats state
  const [aiLlmStats, setAiLlmStats] = useState<AILLMStats[]>([]);
  const [aiLlmStatsLoading, setAiLlmStatsLoading] = useState(false);

  // Repo + user AI stats (from V2 getPrivateChatStats, last 7 days) for top stat cards
  interface RepoUserStats {
    totalRequests: number;
    totalTokensIn: number;
    totalTokensOut: number;
    totalBlocks: number;
    lastLlmModel: string;
    lastLlmType: string;
  }
  const [repoUserStats, setRepoUserStats] = useState<RepoUserStats | null>(null);
  const [repoUserStatsLoading, setRepoUserStatsLoading] = useState(false);

  // Daily AI stats for charts (7 days) – V2 time-series shape
  interface DailyAiStat {
    ts: number;
    tokensIn: number;
    tokensOut: number;
    requests: number;
  }
  const [dailyAiStats, setDailyAiStats] = useState<DailyAiStat[]>([]);
  const [dailyAiStatsLoading, setDailyAiStatsLoading] = useState(false);

  // Policy violations and alerts state
  const [policyViolations, setPolicyViolations] = useState<PolicyTypes.PolicyViolationDTO[]>([]);
  const [policyViolationsTotal, setPolicyViolationsTotal] = useState(0);
  const [policyAlerts, setPolicyAlerts] = useState<PolicyTypes.PolicyAlertDTO[]>([]);
  const [policyAlertsTotal, setPolicyAlertsTotal] = useState(0);
  const [policyStatsLoading, setPolicyStatsLoading] = useState(false);
  
  // Chat filter for telemetry stats
  const [selectedStatsChat, setSelectedStatsChat] = useState<string>('all');
  
  // Store conversation titles from getConversationMessages response
  const [conversationTitles, setConversationTitles] = useState<Record<string, string>>({});

  // Pending first message - used to immediately switch to conversation view
  const [pendingFirstMessage, setPendingFirstMessage] = useState<string | null>(null);

  // Get workspaces from globalData
  const workspaces = globalData?.workspaces || [];
  
  // Check if user has no workspaces, no repos, or no AI configs (usage conditions)
  const hasNoWorkspaces = workspaces.length === 0;
  const hasNoRepos = repos.length === 0;
  const hasNoAiConfigs = aiConfigsCount === 0;
  const cannotInstantiateChat = hasNoWorkspaces || hasNoRepos || hasNoAiConfigs;
  
  // Get missing resource type for blocking modal
  const getMissingResource = () => {
    if (hasNoWorkspaces) return 'workspace';
    if (hasNoRepos) return 'repository';
    if (hasNoAiConfigs) return 'ai';
    return null;
  };

  // Get current workspace ID from centralized hook
  const currentWorkspaceId = useSelectedWorkspace();

  // Check if environment is selected (optional now - user can chat without selecting)
  const isEnvironmentSelected = environmentWorkspaceId && environmentRepoId;
  
  // Get first available repo for auto-creation
  const getFirstAvailableRepo = () => {
    if (selectedRepoId) return selectedRepoId;
    if (environmentRepoId) return environmentRepoId;
    if (repos.length > 0) return repos[0].id;
    return null;
  };

  // Get selected workspace and repo names for header
  const selectedWorkspace = workspaces.find((w: any) => w.id === environmentWorkspaceId);
  const selectedEnvRepo = repos.find((r: any) => r.id === environmentRepoId);

  // Get available AI providers - use availableModels to determine which providers exist
  const getAvailableProviders = useCallback(() => {
    // If we have models loaded for the repo, filter providers based on those
    if (availableModels.length > 0) {
      const modelProviderIds = new Set<AiLlmTypes.AiType>();
      for (const m of availableModels) {
        modelProviderIds.add(m.aiType);
      }
      // Filter main providers based on models (exclude OTHER from initial filter)
      const filtered = AI_PROVIDERS.filter(p => p.id !== AiLlmTypes.AiType.OTHER && modelProviderIds.has(p.id));
      // Always add "Others" at the end - it will show ALL available models when clicked
      const othersProvider = AI_PROVIDERS.find(p => p.id === AiLlmTypes.AiType.OTHER);
      const result = filtered.length > 0 ? [...filtered] : [];
      if (othersProvider && availableModels.length > 0) {
        result.push(othersProvider);
      }
      return result;
    }
    // Fallback: show no providers until models are loaded
    return [];
  }, [availableModels]);

  // Memoized available providers
  const availableProviders = getAvailableProviders();

  // Load repos from API - pass workspaceId to API instead of filtering client-side
  const loadRepos = useCallback(async (workspaceIdOverride?: string) => {
    try {
      setLoadingRepos(true);
      // Use override if provided (for popup workspace selection), otherwise use current workspace
      const wsId = workspaceIdOverride || currentWorkspaceId;
      const workspaceIdParam = wsId && wsId !== 'ALL' ? wsId : undefined;
      const response = (await withTokenRefresh(() => apiClient.getStorageRepos(workspaceIdParam))) as any;
      if (response.success) {
        const allRepos = response.repos || [];
        // Filter to only show repos with at least one AI resource (aiLlms)
        const reposWithAi = allRepos.filter((repo: any) => {
          const aiLlms = repo.aiLlms || [];
          return aiLlms.length > 0;
        });
        console.log('[Chat] Repos with AI resources:', reposWithAi.length, '/', allRepos.length);
        setRepos(reposWithAi);
      }
      setReposLoaded(true);
    } catch (error) {
      console.error('Error loading repos:', error);
      setReposLoaded(true);
    } finally {
      setLoadingRepos(false);
    }
  }, [currentWorkspaceId, withTokenRefresh, apiClient]);

  const loadModelsForRepo = useCallback(
    async (repoId: string) => {
      if (!repoId) return;
      try {
        const response = await withTokenRefresh(() =>
          apiClient.getAiModels({
            repoId,
            llmType: 'chat',
          } as AiLlmTypes.GetAiModelsRequest),
        );
        const typed = response as AiLlmTypes.GetAiModelsResponse;
        if (typed.success) {
          const models = (typed.models || []).map<UiModel>((m) => ({
            ...m,
            provider: AI_PROVIDER_NAMES[m.aiType] || 'Other',
            providerIcon: getProviderIcon(m.aiType),
          }));
          setAvailableModels(models);
          // DEBUG: Log real API model IDs per provider for FEATURED_MODELS mapping
          console.log('[Chat] Available models from API:', models.map(m => ({ id: m.id, name: m.name, aiType: m.aiType })));

          if (!models.find((m) => m.id === selectedModelRef.current) && models.length > 0) {
            setSelectedModel(models[0].id);
          }
        }
      } catch {
        // ignore for now
      }
    },
    [apiClient, withTokenRefresh],
  );

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for openChatEnvironmentModal event from Header
  useEffect(() => {
    const handleOpenModal = () => {
      // Reload repos when modal opens to ensure fresh data
      // Use environmentWorkspaceId if set, otherwise use currentWorkspaceId
      const wsToLoad = environmentWorkspaceId || currentWorkspaceId;
      if (wsToLoad) {
        loadRepos(wsToLoad);
      } else {
        loadRepos();
      }
      setEnvironmentModalOpen(true);
    };
    window.addEventListener('openChatEnvironmentModal', handleOpenModal);
    return () => {
      window.removeEventListener('openChatEnvironmentModal', handleOpenModal);
    };
  }, [loadRepos, environmentWorkspaceId, currentWorkspaceId]);

  // Auto-set workspace if user is already in a specific workspace (not 'ALL')
  useEffect(() => {
    if (currentWorkspaceId && currentWorkspaceId !== 'ALL' && !environmentWorkspaceId) {
      setEnvironmentWorkspaceId(currentWorkspaceId);
      loadRepos(currentWorkspaceId);
    }
  }, [currentWorkspaceId]);

  // Reload repos when environmentWorkspaceId changes and clear selection
  useEffect(() => {
    if (environmentWorkspaceId) {
      // Skip clearing repo if we're explicitly selecting a repo from another workspace
      if (skipRepoResetRef.current) {
        skipRepoResetRef.current = false;
      } else if (repos.length > 0) {
        // Clear repo selection when workspace changes (only if not initial load)
        setEnvironmentRepoId('');
      }
      // Clear selected conversation when workspace changes
      setSelectedConversation(null);
      setMessages([]);
      // Load repos for the new workspace
      loadRepos(environmentWorkspaceId);
    }
  }, [environmentWorkspaceId]);

  // Auto-select first repo when repos are loaded and no repo is selected
  useEffect(() => {
    if (repos.length > 0 && !environmentRepoId && environmentWorkspaceId) {
      setEnvironmentRepoId(repos[0].id);
    }
  }, [repos, environmentWorkspaceId]);

  // Clear conversation when repository changes
  useEffect(() => {
    if (environmentRepoId) {
      // Clear selected conversation when repo changes (new filter applied)
      setSelectedConversation(null);
      setMessages([]);
      loadModelsForRepo(environmentRepoId);
    }
  }, [environmentRepoId, loadModelsForRepo]);

  // Handle ?metrics=true query parameter to open metrics view
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('metrics') === 'true') {
      setMetricsView(true);
      setSelectedConversation(null);
      // Remove the query parameter from URL
      navigate('/chat', { replace: true });
    }
  }, [location.search, navigate]);

  // Load AI LLM Stats when metricsView is active - scoped to current repo's models (V2)
  useEffect(() => {
    let mounted = true;
    const loadAiLlmStats = async () => {
      if (!metricsView || !authState?.token) return;
      try {
        setAiLlmStatsLoading(true);
        const response = await withTokenRefresh(() =>
          apiClient.getAiGatewayBreakdown('models', { scope: 'org', window: '7d' } as any)
        ) as any;
        if (mounted && response?.entities) {
          const allStats: AILLMStats[] = response.entities.map((entity: any) => ({
            aiLlmId: entity.entityId,
            name: entity.entityName || entity.entityId,
            requests: entity.totals?.requests || 0,
            tokensIn: entity.totals?.tokensIn || 0,
            tokensOut: entity.totals?.tokensOut || 0,
          }));
          if (availableModels.length > 0) {
            const repoModelIds = new Set(availableModels.map(m => m.id));
            setAiLlmStats(allStats.filter(s => repoModelIds.has(s.aiLlmId)));
          } else {
            setAiLlmStats(allStats);
          }
        }
      } catch (error) {
        console.error('[Chat] Failed to load AI LLM stats:', error);
      } finally {
        if (mounted) setAiLlmStatsLoading(false);
      }
    };
    loadAiLlmStats();
    return () => { mounted = false; };
  }, [metricsView, authState?.token, withTokenRefresh, apiClient, availableModels]);

  // Load daily AI stats for charts (last 7 days) via V2 getPrivateChatStats
  useEffect(() => {
    let mounted = true;
    const loadDailyAiStats = async () => {
      if (!metricsView || !authState?.token) return;
      try {
        setDailyAiStatsLoading(true);
        const response = await withTokenRefresh(() =>
          apiClient.getPrivateChatStats({ scope: 'org', window: '7d' } as any)
        ) as any;
        if (mounted && response?.series) {
          setDailyAiStats(response.series as DailyAiStat[]);
        }
      } catch (error) {
        console.error('[Chat] Failed to load daily AI stats:', error);
      } finally {
        if (mounted) setDailyAiStatsLoading(false);
      }
    };
    loadDailyAiStats();
    return () => { mounted = false; };
  }, [metricsView, authState?.token, withTokenRefresh, apiClient]);

  // Load repo AI stats (last 7 days) for top stat cards via V2 getPrivateChatStats
  useEffect(() => {
    let mounted = true;
    const loadRepoUserStats = async () => {
      if (!metricsView || !authState?.token || !environmentRepoId) return;
      try {
        setRepoUserStatsLoading(true);
        const response = await withTokenRefresh(() =>
          apiClient.getPrivateChatStats({ scope: 'repo', id: environmentRepoId, window: '7d' } as any)
        ) as any;
        if (!mounted || !response?.success || !response.series?.length) {
          if (mounted) setRepoUserStats(null);
          return;
        }
        const series = response.series;
        const totalRequests = series.reduce((sum: number, p: any) => sum + (p.requests ?? 0), 0);
        const totalTokensIn = series.reduce((sum: number, p: any) => sum + (p.tokensIn ?? 0), 0);
        const totalTokensOut = series.reduce((sum: number, p: any) => sum + (p.tokensOut ?? 0), 0);
        const totalBlocks = series.reduce((sum: number, p: any) => sum + (p.blocks ?? 0), 0);
        const latest = series.slice().sort((a: any, b: any) => (b.ts ?? 0) - (a.ts ?? 0))[0];
        const lastLlmModel = latest?.model ?? '';
        const lastLlmType = latest?.provider ?? '';
        setRepoUserStats({
          totalRequests,
          totalTokensIn,
          totalTokensOut,
          totalBlocks,
          lastLlmModel,
          lastLlmType,
        });
      } catch (error) {
        console.error('[Chat] Failed to load repo/user AI stats:', error);
        if (mounted) setRepoUserStats(null);
      } finally {
        if (mounted) setRepoUserStatsLoading(false);
      }
    };
    loadRepoUserStats();
    return () => { mounted = false; };
  }, [metricsView, authState?.token, environmentRepoId, withTokenRefresh, apiClient]);

  // Load policy violations and alerts when metrics or policies view is shown
  useEffect(() => {
    if ((!metricsView && !policiesView) || !authState?.token) return;
    
    let mounted = true;
    const loadPolicyStats = async () => {
      setPolicyStatsLoading(true);
      try {
        // Fetch all violations/alerts (no repo filter for private chat)
        const violationParams: any = {
          take: 50,
          from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        };
        const alertParams: any = {
          take: 50,
          from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        };

        // Load violations (last 30 days)
        const violationsResponse = await withTokenRefresh(() => 
          apiClient.getPolicyViolations(violationParams)
        );
        console.log('[Chat] Policy violations response:', violationsResponse);
        if (mounted && violationsResponse.success) {
          setPolicyViolations(violationsResponse.violations || []);
          setPolicyViolationsTotal(violationsResponse.total || 0);
        }

        // Load alerts (last 30 days)
        const alertsResponse = await withTokenRefresh(() => 
          apiClient.getPolicyAlerts(alertParams)
        );
        console.log('[Chat] Policy alerts response:', alertsResponse);
        if (mounted && alertsResponse.success) {
          setPolicyAlerts(alertsResponse.alerts || []);
          setPolicyAlertsTotal(alertsResponse.total || 0);
        }
      } catch (error) {
        console.error('[Chat] Failed to load policy stats:', error);
      } finally {
        if (mounted) setPolicyStatsLoading(false);
      }
    };
    loadPolicyStats();
    return () => { mounted = false; };
  }, [metricsView, policiesView, authState?.token, withTokenRefresh, apiClient]);

  // Repo AI stats (from V2 getPrivateChatStats, last 7 days) - used for top stat cards and policy section
  const getRepoUserStats = () => {
    if (!repoUserStats) {
      return {
        totalRequests: 0,
        totalTokensIn: 0,
        totalTokensOut: 0,
        totalBlocks: 0,
        lastModelDisplay: 'N/A',
      };
    }
    const lastModelDisplay = repoUserStats.lastLlmModel || repoUserStats.lastLlmType || 'N/A';
    return {
      totalRequests: repoUserStats.totalRequests,
      totalTokensIn: repoUserStats.totalTokensIn,
      totalTokensOut: repoUserStats.totalTokensOut,
      totalBlocks: repoUserStats.totalBlocks,
      lastModelDisplay,
    };
  };

  // Format number helper for stats display
  const formatStatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  useEffect(() => {
    if (authState?.token) {
      loadRepos();
    }
  }, [authState?.token, loadRepos]);
  
  // Load AI configs count for usage conditions check
  useEffect(() => {
    const loadAiConfigs = async () => {
      if (!authState?.token) return;
      try {
        const response = await withTokenRefresh(() => apiClient.getAiLlms());
        if ((response as any)?.llms) {
          setAiConfigsCount((response as any).llms.length);
        }
        setAiConfigsLoaded(true);
      } catch (error) {
        console.error('[Chat] Error loading AI configs:', error);
        setAiConfigsLoaded(true);
      }
    };
    loadAiConfigs();
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Load all workspaces repos count to find workspaces with repos
  useEffect(() => {
    const loadWorkspacesWithRepos = async () => {
      if (!authState?.token || workspaces.length === 0) return;
      try {
        // Load all repos without workspace filter to get all repos
        const response = await withTokenRefresh(() => apiClient.getStorageRepos());
        if ((response as any)?.repos) {
          const rawRepos = (response as any).repos;
          // Filter to only repos with at least one AI resource (aiLlms)
          const allRepos = rawRepos.filter((repo: any) => {
            const aiLlms = repo.aiLlms || [];
            return aiLlms.length > 0;
          });
          console.log('[Chat] Environment repos with AI:', allRepos.length, '/', rawRepos.length);
          
          // Group repos by workspace and find workspaces that have repos with AI
          const wsWithRepos = workspaces.filter((ws: any) => 
            allRepos.some((repo: any) => {
              const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
              return String(wid) === String(ws.id);
            })
          );
          setWorkspacesWithRepos(wsWithRepos);
          
          // Group all repos by workspace for unified environment list
          const grouped = workspaces.map((ws: any) => ({
            workspace: ws,
            repos: allRepos.filter((repo: any) => {
              const wid = repo.workspaceId || repo.workspace_id || repo.workspace?.id || repo.workspace;
              return String(wid) === String(ws.id);
            })
          })).filter(g => g.repos.length > 0);
          setAllReposGrouped(grouped);
        }
      } catch (error) {
        console.error('[Chat] Error loading workspaces with repos:', error);
      }
    };
    loadWorkspacesWithRepos();
  }, [authState?.token, workspaces, withTokenRefresh, apiClient]);

  // Check if user has no resources and show tutorial (non-blocking)
  // Order: 1. Welcome to Private Chat (if new user), 2. Check resources
  useEffect(() => {
    // Wait for repos, AI configs, AND org-level conversation check to finish before checking
    if (!resourcesChecked && reposLoaded && aiConfigsLoaded && !loading && globalData?.isReady && hasOrgConversations !== null) {
      setResourcesChecked(true);
      
      // First: Show Welcome to Private Chat if not shown before
      // Skip if user already has conversations in ANY workspace (they already know the service)
      try {
        const tutorialShown = localStorage.getItem('privateChatTutorialShown');
        if (!tutorialShown && !hasOrgConversations) {
          // Show Welcome to Private Chat first
          setShowChatTutorial(true);
          return; // Don't check resources yet - will be checked after tutorial completion
        } else if (!tutorialShown && hasOrgConversations) {
          // User has conversations - auto-mark as shown
          localStorage.setItem('privateChatTutorialShown', 'true');
        }
      } catch { /* ignore */ }
      
      // Second: Check resources after welcome tutorial is done
      if (hasNoWorkspaces) {
        setNoResourcesModalOpen(true);
      } else if (hasNoRepos) {
        // Current workspace has no repos - check if other workspaces have repos
        if (workspacesWithRepos.length > 0) {
          // Suggest switching to a workspace with repos
          setSuggestWorkspaceModalOpen(true);
        } else {
          // No workspace has repos - show blocking modal
          setNoResourcesModalOpen(true);
        }
      }
    }
  }, [resourcesChecked, reposLoaded, aiConfigsLoaded, loading, hasNoWorkspaces, hasNoRepos, workspacesWithRepos, globalData?.isReady, hasOrgConversations]);
  
  // Handle tutorial completion - then check resources
  const handleChatTutorialComplete = () => {
    setShowChatTutorial(false);
    try {
      localStorage.setItem('privateChatTutorialShown', 'true');
    } catch { /* ignore */ }
    
    // After welcome tutorial, check if user has resources
    if (hasNoWorkspaces) {
      setNoResourcesModalOpen(true);
    } else if (hasNoRepos) {
      if (workspacesWithRepos.length > 0) {
        setSuggestWorkspaceModalOpen(true);
      } else {
        setNoResourcesModalOpen(true);
      }
    }
  };

  // Check if user has ANY conversations at org level (for Welcome popup decision)
  // This runs once on mount, without workspace filtering
  useEffect(() => {
    if (!authState?.token || !globalData?.userProfile?.id) return;
    if (hasOrgConversations !== null) return; // Already checked
    
    const checkOrgConversations = async () => {
      try {
        const params: ConversationTypes.GetConversationsRequest = {
          take: 1, // Only need to know if at least one exists
          userId: globalData.userProfile!.id,
          showDeleted: false,
          // No workspaceId filter - check all workspaces
        };
        const response = await withTokenRefresh(() => apiClient.getConversations(params));
        const hasAny = (response.conversations || []).length > 0;
        setHasOrgConversations(hasAny);
        console.log('[Chat] Org-level conversation check:', hasAny ? 'has conversations' : 'no conversations');
      } catch (error) {
        console.error('[Chat] Error checking org conversations:', error);
        setHasOrgConversations(false); // Assume no conversations on error
      }
    };
    checkOrgConversations();
  }, [authState?.token, globalData?.userProfile?.id, hasOrgConversations, withTokenRefresh, apiClient]);

  // Load conversations (sidebar list and metrics view use this; filtered by workspace/repo from environment popup)
  useEffect(() => {
    if (!authState?.token) return;
    let mounted = true;
    const loadConversations = async () => {
      try {
        setLoading(true);
        const params: ConversationTypes.GetConversationsRequest = {
          take: 50,
          userId: globalData?.userProfile?.id || '',
          showDeleted: false,
        };
        const workspaceToUse = environmentWorkspaceId || currentWorkspaceId;
        if (workspaceToUse && workspaceToUse !== 'ALL') {
          params.workspaceId = workspaceToUse;
        }
        if (environmentRepoId) {
          params.repoId = environmentRepoId;
        }
        const response = await withTokenRefresh(() => apiClient.getConversations(params));
        if (mounted) {
          setConversations(response.conversations || []);
        }
      } catch (error) {
        if (mounted) console.error('[Chat] Error loading conversations:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadConversations();
    return () => { mounted = false; };
  }, [authState?.token, environmentRepoId, environmentWorkspaceId, currentWorkspaceId, withTokenRefresh, apiClient]);

  // Load messages when conversation is selected
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedConversation) {
        setMessages([]);
        return;
      }

      // Skip if messages were already loaded (e.g. after conversation creation)
      if (skipNextMessageLoadRef.current) {
        skipNextMessageLoadRef.current = false;
        return;
      }

      try {
        setLoadingMessages(true);
        const response = await withTokenRefresh(() => 
          apiClient.getConversationMessages(selectedConversation.id, { take: 50, skip: 0 })
        );
        setMessages(response.messages || []);
        // Store title if it exists and is not empty
        if (response.title && response.title !== '') {
          setConversationTitles(prev => ({
            ...prev,
            [selectedConversation.id]: response.title!
          }));
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        setMessages([]);
      } finally {
        setLoadingMessages(false);
      }
    };

    loadMessages();
  }, [selectedConversation, authState?.token, withTokenRefresh, apiClient]);

  // Create new conversation and send first message
  const handleCreateConversationWithMessage = async (messageText: string) => {
    const repoId = getFirstAvailableRepo();
    if (!repoId) {
      // No repo available, open environment modal
      setEnvironmentModalOpen(true);
      return;
    }

    try {
      setCreatingConversation(true);
      setSending(true);
      // Immediately switch to conversation view with user's message visible
      setPendingFirstMessage(messageText);
      setNewMessage('');
      
      // Create conversation
      const response = await withTokenRefresh(() => apiClient.createConversation({
        repoId: repoId,
      }));

      if (response.success && response.conversationId) {
        // Immediately construct a conversation object so we don't depend on the list reload
        const tempConv: ConversationTypes.ConversationDTO = {
          id: response.conversationId,
          repoId: repoId,
          title: '',
          createdAt: new Date().toISOString(),
          workspaceId: currentWorkspaceId || '',
        } as ConversationTypes.ConversationDTO;

        // Set the conversation immediately so UI stays in conversation view
        skipNextMessageLoadRef.current = true;
        setSelectedConversation(tempConv);
        // Add to conversations list immediately so it appears in sidebar
        setConversations(prev => [tempConv, ...prev]);

        // Send the first message
        await withTokenRefresh(() => apiClient.sendPrompt(response.conversationId!, {
          prompt: messageText,
          model: selectedModel,
        }));
        
        // Load messages for the new conversation
        const messagesResponse = await withTokenRefresh(() => 
          apiClient.getConversationMessages(response.conversationId!, { take: 50, skip: 0 })
        );
        setMessages(messagesResponse.messages || []);
        // Clear pending first message now that real messages are loaded
        setPendingFirstMessage(null);
        // Store title if it exists and is not empty
        if (messagesResponse.title && messagesResponse.title !== '') {
          setConversationTitles(prev => ({
            ...prev,
            [response.conversationId!]: messagesResponse.title!
          }));
        }

        // Reload conversations list in background to sync sidebar with real data
        const convId = response.conversationId!;
        const bgParams: ConversationTypes.GetConversationsRequest = {
          take: 50,
          userId: globalData?.userProfile?.id || '',
          showDeleted: false,
        };
        const bgWorkspace = environmentWorkspaceId || currentWorkspaceId;
        if (bgWorkspace && bgWorkspace !== 'ALL') {
          bgParams.workspaceId = bgWorkspace;
        }
        if (environmentRepoId) {
          bgParams.repoId = environmentRepoId;
        }
        withTokenRefresh(() => apiClient.getConversations(bgParams)).then(convResponse => {
          const serverList = convResponse.conversations || [];
          const realConv = serverList.find(
            (c: ConversationTypes.ConversationDTO) => c.id === convId
          );
          if (realConv) {
            // Server has the new conversation - use the server list as-is
            setConversations(serverList);
            skipNextMessageLoadRef.current = true;
            setSelectedConversation(realConv);
          } else {
            // Server list doesn't include the new conversation yet (race condition)
            // Merge: keep the temp conversation at top + server list without duplicates
            setConversations(prev => {
              const tempConvEntry = prev.find(c => c.id === convId);
              if (tempConvEntry) {
                return [tempConvEntry, ...serverList.filter(c => c.id !== convId)];
              }
              return serverList;
            });
          }
        }).catch(() => { /* ignore background refresh errors */ });

        setSelectedRepoId('');
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      // Clear pending message on error so user can try again
      setPendingFirstMessage(null);
    } finally {
      setCreatingConversation(false);
      setSending(false);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Optimistically add user message
    const tempUserMessage: ConversationTypes.MessageDTO = {
      id: `temp-${Date.now()}`,
      conversationId: selectedConversation.id,
      role: 'user',
      content: messageText,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    try {
      await withTokenRefresh(() => apiClient.sendPrompt(selectedConversation.id, {
        prompt: messageText,
        model: selectedModel,
      }));

      // Reload messages to get the assistant response
      const messagesResponse = await withTokenRefresh(() => 
        apiClient.getConversationMessages(selectedConversation.id, { take: 50, skip: 0 })
      );
      setMessages(messagesResponse.messages || []);
      // Store title if it exists and is not empty
      if (messagesResponse.title && messagesResponse.title !== '') {
        setConversationTitles(prev => ({
          ...prev,
          [selectedConversation.id]: messagesResponse.title!
        }));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
    } finally {
      setSending(false);
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId: string) => {
    try {
      await withTokenRefresh(() => apiClient.deleteConversation({
        conversationId: conversationId,
        hardDelete: false, // Regular users can only soft delete
      }));
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get filtered conversations based on environment repo
  const filteredConversations = environmentRepoId 
    ? conversations.filter(c => c.repoId === environmentRepoId)
    : conversations;

  // Get selected repo name
  const getRepoName = (repoId: string) => {
    const repo = repos.find(r => r.id === repoId);
    return repo?.name || 'Unknown Repository';
  };

  // Get conversation display title (uses DTO title if available, otherwise repo name)
  const getConversationTitle = (conv: ConversationTypes.ConversationDTO) => {
    if (conv.title && conv.title !== '') {
      return conv.title;
    }
    return getRepoName(conv.repoId);
  };

  // Show upgrade prompt for individual accounts
  if (!globalData?.hasOrgAccess) {
    return (
      <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', overflow: 'auto', bgcolor: '#FFFFFF' }}>
        <OrganizationUpgradePrompt
          title="Private Chat"
          description="Engage in secure conversations with your enterprise-grade AI chat. Use your organization's approved models, policies, and AI environment for a fully private and compliant experience."
          featureName="Private Chat"
          features={[
            {
              title: 'Organization-Scoped AI Environment',
              description: "Private Chat runs entirely within your organization's configured AI environment, using only approved models, policies, and system prompts — no cross-tenant leakage.",
            },
            {
              title: 'Data Privacy & Compliance by Design',
              description: "Conversations are not used for model training and follow your organization's data-handling, retention, and compliance rules (GDPR, SOC-2–ready workflows, etc.).",
            },
            {
              title: 'Multi-Model & Policy Control',
              description: 'Seamlessly switch between allowed AI models and policy profiles defined by your organization, enabling different use cases (exploration, production, sensitive data).',
            },
            {
              title: 'Secure, Isolated Conversations',
              description: 'Each chat session is isolated, encrypted, and access-controlled, ensuring confidential discussions stay private and auditable within your workspace.',
            },
          ]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left Sidebar - Conversations (Collapsible) */}
      <Box sx={{ 
        width: sidebarCollapsed ? 0 : 300, 
        minWidth: sidebarCollapsed ? 0 : 300,
        flexShrink: 0, 
        bgcolor: '#FAFAFA',
        borderRight: sidebarCollapsed ? 'none' : '1px solid #E9EAEB',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width 0.3s ease, min-width 0.3s ease',
        zIndex: 30,
        position: 'relative',
      }}>

        {/* Header with title and collapse button */}
        <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#101828' }}>
              Private Chat
            </Typography>
          </Box>
        </Box>

        {/* Icon Buttons: Metrics, Policies | New Chat */}
        <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Left group: Metrics, Policies */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => {
                setMetricsView(!metricsView);
                setPoliciesOpen(false);
                setPoliciesView(false);
                setSelectedConversation(null);
              }}
              title="Token Usage"
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                border: '1px solid #E5E7EB',
                color: metricsView ? '#617BFF' : '#667085',
                bgcolor: metricsView ? '#F0F2FF' : 'transparent',
                '&:hover': { borderColor: '#617BFF', color: '#617BFF', bgcolor: '#F0F2FF' },
              }}
            >
              <BarChartIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          
          {/* Vertical separator */}
          <Box sx={{ width: '1px', height: 28, bgcolor: '#E5E7EB', mx: 1 }} />
          
          {/* Right: New Chat button */}
          <IconButton
            onClick={() => {
              setSelectedConversation(null);
              setMessages([]);
              setPoliciesOpen(false);
              setPoliciesView(false);
              setMetricsView(false);
            }}
            title="New Chat"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: '#617BFF',
              color: 'white',
              '&:hover': { bgcolor: '#5A6FE8' },
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Search Input - Always visible */}
        <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: searchQuery && (
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: 'white',
                fontSize: '13px',
                '& fieldset': { borderColor: '#E5E7EB' },
                '&:hover fieldset': { borderColor: '#617BFF' },
                '&.Mui-focused fieldset': { borderColor: '#617BFF' },
              },
            }}
          />
        </Box>

        {/* Policies Panel */}
        {policiesOpen && (
          <Box sx={{ p: 2, borderBottom: '1px solid #E9EAEB', bgcolor: '#F9FAFB' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Repository Policies
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {selectedEnvRepo?.policies && selectedEnvRepo.policies.length > 0 ? (
                selectedEnvRepo.policies.map((policy: any, idx: number) => (
                  <Box key={idx} sx={{ p: 1.5, bgcolor: 'white', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                      {policy.name || policy.type || `Policy ${idx + 1}`}
                    </Typography>
                    {policy.description && (
                      <Typography sx={{ fontSize: '11px', color: '#667085', mt: 0.5 }}>
                        {policy.description}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <PolicyIcon sx={{ fontSize: 24, color: '#D0D5DD', mb: 0.5 }} />
                  <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                    {selectedEnvRepo ? 'No policies configured' : 'Select a repository to view policies'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Recent Chats Section */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #E9EAEB' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mb: 0.5,
          }}>
            <HistoryIcon sx={{ fontSize: 16, color: '#667085' }} />
            <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Recent Chats
            </Typography>
          </Box>
        </Box>

        {/* Conversations List */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 1.5,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#E5E7EB', borderRadius: '2px' },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#D0D5DD' },
          scrollbarWidth: 'thin',
          scrollbarColor: '#E5E7EB transparent',
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} sx={{ color: '#617BFF' }} />
            </Box>
          ) : filteredConversations.filter(c => 
              !searchQuery || 
              getConversationTitle(c).toLowerCase().includes(searchQuery.toLowerCase()) ||
              getRepoName(c.repoId).toLowerCase().includes(searchQuery.toLowerCase()) ||
              c.id.toLowerCase().includes(searchQuery.toLowerCase())
            ).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
              <ChatIcon sx={{ fontSize: 32, color: '#D0D5DD', mb: 1 }} />
              <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                {environmentRepoId ? 'No conversations for this repository' : 'No conversations yet'}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#98A2B3', mt: 0.5 }}>
                {environmentRepoId ? 'Start a new chat to begin' : 'Select an environment to start'}
              </Typography>
            </Box>
          ) : (
            filteredConversations
              .filter(c => 
                !searchQuery || 
                getConversationTitle(c).toLowerCase().includes(searchQuery.toLowerCase()) ||
                getRepoName(c.repoId).toLowerCase().includes(searchQuery.toLowerCase()) ||
                c.id.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((conv) => (
              <Box
                key={conv.id}
                onClick={() => {
                  setSelectedConversation(conv);
                  setMetricsView(false);
                  setPoliciesView(false);
                }}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: '10px',
                  cursor: 'pointer',
                  bgcolor: selectedConversation?.id === conv.id ? '#F0F2FF' : 'white',
                  border: selectedConversation?.id === conv.id ? '1px solid #617BFF' : '1px solid #E5E7EB',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  '&:hover': {
                    bgcolor: selectedConversation?.id === conv.id ? '#F0F2FF' : '#F9FAFB',
                  },
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ 
                    fontSize: '13px', 
                    fontWeight: 500, 
                    color: '#101828',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {getConversationTitle(conv)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setChatMenuAnchor(e.currentTarget);
                    setChatMenuConv(conv);
                  }}
                  sx={{ 
                    color: '#98A2B3',
                    transition: 'opacity 0.15s',
                    '&:hover': { color: '#617BFF', bgcolor: '#F0F2FF' },
                  }}
                  className="chat-menu-btn"
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Chat Item Menu */}
      <Menu
        anchorEl={chatMenuAnchor}
        open={Boolean(chatMenuAnchor)}
        onClose={() => {
          setChatMenuAnchor(null);
          setChatMenuConv(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            minWidth: 160,
            borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            border: '1px solid #E5E7EB',
            py: 0.5,
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            setDetailsDialogOpen(true);
            setChatMenuAnchor(null);
          }}
          sx={{ fontSize: '13px', py: 1, px: 2, gap: 1.5 }}
        >
          <InfoIcon sx={{ fontSize: 16, color: '#667085' }} />
          Details
        </MenuItem>
        <MenuItem 
          onClick={() => {
            setRenameValue(chatMenuConv ? getRepoName(chatMenuConv.repoId) : 'Chat');
            setRenameDialogOpen(true);
            setChatMenuAnchor(null);
          }}
          sx={{ fontSize: '13px', py: 1, px: 2, gap: 1.5 }}
        >
          <EditIcon sx={{ fontSize: 16, color: '#667085' }} />
          Rename
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (chatMenuConv) {
              handleDeleteConversation(chatMenuConv.id);
            }
            setChatMenuAnchor(null);
            setChatMenuConv(null);
          }}
          sx={{ fontSize: '13px', py: 1, px: 2, gap: 1.5, color: '#F04438', '&:hover': { bgcolor: '#FEF3F2' } }}
        >
          <DeleteIcon sx={{ fontSize: 16 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Modal
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
      >
        <Fade in={renameDialogOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'white',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            p: 3,
          }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#101828', mb: 2 }}>
              Rename conversation
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Enter new name..."
              autoFocus
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#F9FAFB',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#617BFF' },
                  '&.Mui-focused fieldset': { borderColor: '#617BFF', borderWidth: '1px' },
                },
                '& .MuiOutlinedInput-input': {
                  fontSize: '14px',
                  padding: '10px 14px',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={() => setRenameDialogOpen(false)}
                sx={{
                  borderColor: '#E5E7EB',
                  color: '#667085',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '13px',
                  '&:hover': { borderColor: '#617BFF', color: '#617BFF' },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  // TODO: Call API to rename conversation
                  console.log('Rename conversation to:', renameValue);
                  setRenameDialogOpen(false);
                }}
                sx={{
                  bgcolor: '#617BFF',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#5A6FE8' },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Details Dialog */}
      <Modal
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
      >
        <Fade in={detailsDialogOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            bgcolor: 'white',
            borderRadius: '12px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            p: 3,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#101828' }}>
                Conversation Details
              </Typography>
              <IconButton size="small" onClick={() => setDetailsDialogOpen(false)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            
            {chatMenuConv && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {/* Conversation Info Section */}
                <Box sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                    Conversation
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Name</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                        {getRepoName(chatMenuConv.repoId)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Created</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                        {formatDate(chatMenuConv.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>ID</Typography>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#101828', fontFamily: 'monospace' }}>
                        {chatMenuConv.id.slice(0, 16)}...
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Repository Section */}
                <Box sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                    Repository
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Name</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FolderIcon sx={{ fontSize: 14, color: '#617BFF' }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#617BFF' }}>
                          {getRepoName(chatMenuConv.repoId)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Type</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                        {(() => {
                          const repo = repos.find((r: any) => r.id === chatMenuConv.repoId);
                          return repo?.storageType || repo?.mode || 'Standard';
                        })()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Workspace</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                        {(() => {
                          const workspace = workspaces.find((w: any) => w.id === chatMenuConv.workspaceId);
                          return workspace?.name || 'Default';
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                {/* Model Section */}
                <Box sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                    AI Model
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Current Model</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(() => {
                          const model = availableModels.find(m => m.id === selectedModel);
                          return (
                            <>
                              <span style={{ display: 'flex', alignItems: 'center', color: '#617BFF' }}>
                                {model ? model.providerIcon : getProviderIcon(AiLlmTypes.AiType.OPENAI)}
                              </span>
                              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                                {model?.name || 'GPT-4'}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>Provider</Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                        {availableModels.find(m => m.id === selectedModel)?.provider || 'OpenAI'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Main Chat Area */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'visible',
        bgcolor: '#FAFAFA',
        position: 'relative',
      }}>

        {/* Metrics View */}
        {metricsView ? (
          <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
            <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
              {/* Header with date selector */}
              <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    onClick={() => setMetricsView(false)}
                    title="Return to Chat"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      color: '#667085',
                      '&:hover': { borderColor: '#617BFF', color: '#617BFF', bgcolor: '#F0F2FF' },
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                  </IconButton>
                  <Box>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#101828', mb: 0.5 }}>
                      Token Usage
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                      Token consumption and usage analytics for your private conversations
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {/* Period Label - Fixed to 7 days */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 0.75,
                    bgcolor: '#F3F4F6', 
                    borderRadius: '8px',
                  }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828' }}>
                      Last 7 days
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Main Stats Cards - Row 1 */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3, mb: 3 }}>
                {(() => {
                  const aggregated = getRepoUserStats();
                  const totalTokens = aggregated.totalTokensIn + aggregated.totalTokensOut;
                  const conversationsCount = filteredConversations.length;
                  return [
                    { label: 'Requests', value: repoUserStatsLoading ? '...' : formatStatNumber(aggregated.totalRequests), change: '', icon: ChatIcon, color: '#617BFF' },
                    { label: 'Tokens', value: repoUserStatsLoading ? '...' : formatStatNumber(totalTokens), change: '', icon: SpeedIcon, color: '#10B981' },
                    { label: 'Conversations', value: formatStatNumber(conversationsCount), change: '', icon: HistoryIcon, color: '#F59E0B' },
                    { label: 'Last Most Used Model', value: repoUserStatsLoading ? '...' : aggregated.lastModelDisplay, change: '', icon: SmartToyIcon, color: '#8B5CF6', isText: true },
                  ];
                })().map((stat, idx) => (
                  <Box key={idx} sx={{ 
                    bgcolor: 'white', 
                    borderRadius: '12px', 
                    border: '1px solid #E5E7EB',
                    p: 2.5,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ 
                        width: 36, 
                        height: 36, 
                        borderRadius: '8px', 
                        bgcolor: `${stat.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <stat.icon sx={{ fontSize: 18, color: stat.color }} />
                      </Box>
                      <Typography sx={{ 
                        fontSize: '11px', 
                        fontWeight: 600, 
                        color: stat.change.startsWith('+') && !stat.label.includes('Error') ? '#10B981' : stat.change.startsWith('-') ? '#10B981' : '#EF4444',
                        bgcolor: stat.change.startsWith('+') && !stat.label.includes('Error') ? '#ECFDF5' : stat.change.startsWith('-') ? '#ECFDF5' : '#FEF2F2',
                        px: 1,
                        py: 0.25,
                        borderRadius: '4px',
                      }}>
                        {stat.change}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828', mb: 0.25 }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#667085' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Token Stats - Row 2 - HIDDEN */}
              {false && (() => {
                const aggregated = getRepoUserStats();
                const totalTokens = aggregated.totalTokensIn + aggregated.totalTokensOut;
                const avgTokensPerRequest = aggregated.totalRequests > 0 ? Math.round(aggregated.totalTokensIn / aggregated.totalRequests) : 0;
                const avgTokensPerResponse = aggregated.totalRequests > 0 ? Math.round(aggregated.totalTokensOut / aggregated.totalRequests) : 0;
                return (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3, mb: 4 }}>
                    <Box sx={{ 
                      bgcolor: 'white', 
                      borderRadius: '12px', 
                      border: '1px solid #E5E7EB',
                      p: 2.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#617BFF' }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Tokens In (Input)
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                        {formatStatNumber(aggregated.totalTokensIn)}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                        ~{formatStatNumber(avgTokensPerRequest)} tokens/request avg
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      bgcolor: 'white', 
                      borderRadius: '12px', 
                      border: '1px solid #E5E7EB',
                      p: 2.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Tokens Out (Output)
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                        {formatStatNumber(aggregated.totalTokensOut)}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                        ~{formatStatNumber(avgTokensPerResponse)} tokens/response avg
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      bgcolor: 'white', 
                      borderRadius: '12px', 
                      border: '1px solid #E5E7EB',
                      p: 2.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F59E0B' }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Tokens
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                        {formatStatNumber(totalTokens)}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                        This billing period
                      </Typography>
                    </Box>
                    <Box sx={{ 
                      bgcolor: 'white', 
                      borderRadius: '12px', 
                      border: '1px solid #E5E7EB',
                      p: 2.5,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#8B5CF6' }} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          LLM Tokens In
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                        {formatStatNumber(aggregated.totalTokensIn)}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
                        Internal LLM processing
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}

              {/* Charts Row - HIDDEN */}
              {false && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 4 }}>
                {/* Invocations Over Time */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                      Invocations Over Time
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#617BFF' }} />
                        <Typography sx={{ fontSize: '11px', color: '#667085' }}>Requests</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
                        <Typography sx={{ fontSize: '11px', color: '#667085' }}>Success</Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ height: 160, display: 'flex', alignItems: 'flex-end', gap: 0.5, position: 'relative' }}>
                    {[40, 65, 45, 80, 55, 70, 90, 60, 75, 85, 50, 95, 88, 72].map((h, i) => (
                      <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                        <Box sx={{ 
                          width: '100%', 
                          height: `${h * 1.5}px`, 
                          bgcolor: '#617BFF',
                          borderRadius: '3px 3px 0 0',
                          position: 'relative',
                        }}>
                          <Box sx={{ 
                            position: 'absolute', 
                            bottom: 0, 
                            left: 0, 
                            right: 0, 
                            height: `${h * 1.4}px`, 
                            bgcolor: '#10B981',
                            borderRadius: '3px 3px 0 0',
                          }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, px: 0.5 }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                      <Typography key={i} sx={{ fontSize: '10px', color: '#9CA3AF' }}>{d}</Typography>
                    ))}
                  </Box>
                </Box>

                {/* Latency Distribution */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 3 }}>
                    Latency Distribution
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      { label: 'p50', value: '1.2s', pct: 50, color: '#10B981' },
                      { label: 'p90', value: '2.1s', pct: 70, color: '#F59E0B' },
                      { label: 'p95', value: '3.4s', pct: 85, color: '#F97316' },
                      { label: 'p99', value: '5.8s', pct: 95, color: '#EF4444' },
                    ].map((item, i) => (
                      <Box key={i}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#344054' }}>
                            {item.label}
                          </Typography>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>
                            {item.value}
                          </Typography>
                        </Box>
                        <Box sx={{ height: 6, bgcolor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' }}>
                          <Box sx={{ width: `${item.pct}%`, height: '100%', bgcolor: item.color, borderRadius: '3px' }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>}

              {/* Top 5 Models Table - Last 7 days */}
              <Box sx={{ 
                bgcolor: 'white', 
                borderRadius: '12px', 
                border: '1px solid #E5E7EB',
                p: 3,
                mb: 4,
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                    Top models
                  </Typography>
                </Box>
                <Box sx={{ overflow: 'hidden', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  {/* Table Header */}
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '2fr 1fr 1fr 1fr', 
                    gap: 2, 
                    p: 1.5, 
                    bgcolor: '#F9FAFB',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Model Name
                    </Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>
                      Tokens In
                    </Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>
                      Tokens Out
                    </Typography>
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>
                      Requests
                    </Typography>
                  </Box>
                  {/* Table Body - Connected to real aiLlmStats data */}
                  {aiLlmStats.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                        {aiLlmStatsLoading ? 'Loading...' : 'No model usage data available'}
                      </Typography>
                    </Box>
                  ) : aiLlmStats
                    .map(stat => {
                      const model = availableModels.find(m => m.id === stat.aiLlmId);
                      const aiLlmIdLower = (stat.aiLlmId || '').toLowerCase();
                      const detectedProvider = aiLlmIdLower.includes('gpt') || aiLlmIdLower.includes('openai') ? AiLlmTypes.AiType.OPENAI
                        : aiLlmIdLower.includes('claude') || aiLlmIdLower.includes('anthropic') ? AiLlmTypes.AiType.ANTHROPIC
                        : aiLlmIdLower.includes('gemini') || aiLlmIdLower.includes('google') ? AiLlmTypes.AiType.GOOGLE
                        : aiLlmIdLower.includes('aws') || aiLlmIdLower.includes('bedrock') || aiLlmIdLower.includes('nova') || aiLlmIdLower.includes('ministral') ? AiLlmTypes.AiType.AWS
                        : null;
                      const providerType = model?.aiType || detectedProvider || AiLlmTypes.AiType.OTHER;
                      const providerName = AI_PROVIDER_NAMES[providerType] || 'Other';
                      let modelName = model?.name || '';
                      if (!modelName) {
                        modelName = (stat.aiLlmId || providerName)
                          .replace(/-\d{8,}$/, '')
                          .split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      }
                      return {
                        name: modelName,
                        provider: providerName,
                        providerType,
                        tokensIn: stat.tokensIn || 0,
                        tokensOut: stat.tokensOut || 0,
                        requests: stat.requests || 0,
                        icon: getProviderIcon(providerType),
                      };
                    })
                    .filter(m => m.requests >= 1)
                    .sort((a, b) => b.requests - a.requests)
                    .map((model, i, arr) => (
                    <Box 
                      key={i} 
                      sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '2fr 1fr 1fr 1fr', 
                        gap: 2, 
                        p: 1.5, 
                        borderBottom: i < arr.length - 1 ? '1px solid #F3F4F6' : 'none',
                        '&:hover': { bgcolor: '#F9FAFB' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0 }}>
                          {model.icon}
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                          {model.name} <span style={{ color: '#667085', fontWeight: 400 }}>— {model.provider}</span>
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#617BFF', textAlign: 'right', alignSelf: 'center' }}>
                        {formatStatNumber(model.tokensIn)}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#10B981', textAlign: 'right', alignSelf: 'center' }}>
                        {formatStatNumber(model.tokensOut)}
                      </Typography>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#8B5CF6', textAlign: 'right', alignSelf: 'center' }}>
                        {formatStatNumber(model.requests)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Stats Charts Row - Conversations, Tokens, Requests (7d) */}
              {(() => {
                const today = new Date();
                const last7Days = Array.from({ length: 7 }, (_, i) => {
                  const date = new Date(today);
                  date.setDate(today.getDate() - (6 - i));
                  return date;
                });
                const dayLabels = last7Days.map(d => d.toLocaleDateString('en-US', { weekday: 'short' }));
                
                // Conversations per day
                const conversationsPerDay = last7Days.map(day => {
                  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate());
                  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
                  return filteredConversations.filter(conv => {
                    const createdAt = conv.createdAt ? new Date(conv.createdAt) : null;
                    return createdAt && createdAt >= dayStart && createdAt < dayEnd;
                  }).length;
                });
                const maxConversations = Math.max(...conversationsPerDay, 1);
                
                // Tokens per day (from dailyAiStats V2)
                const tokensPerDay = last7Days.map(day => {
                  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime() / 1000;
                  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime() / 1000;
                  return dailyAiStats
                    .filter(stat => stat.ts >= dayStart && stat.ts < dayEnd)
                    .reduce((sum, stat) => sum + (stat.tokensIn || 0) + (stat.tokensOut || 0), 0);
                });
                const totalTokens7d = tokensPerDay.reduce((sum, t) => sum + t, 0);
                const maxTokens = Math.max(...tokensPerDay, 1);
                
                // Requests per day (from dailyAiStats V2)
                const requestsPerDay = last7Days.map(day => {
                  const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime() / 1000;
                  const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime() / 1000;
                  return dailyAiStats
                    .filter(stat => stat.ts >= dayStart && stat.ts < dayEnd)
                    .reduce((sum, stat) => sum + (stat.requests || 0), 0);
                });
                const totalRequests7d = requestsPerDay.reduce((sum, r) => sum + r, 0);
                const maxRequests = Math.max(...requestsPerDay, 1);
                
                return (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, mb: 4 }}>
                {/* Conversations Created */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 3 }}>
                    Conversations Created
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>{filteredConversations.length}</Typography>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Last 7 days</Typography>
                    </Box>
                    <Box sx={{ height: 80, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      {conversationsPerDay.map((count, i) => (
                        <Box 
                          key={i} 
                          sx={{ 
                            flex: 1, 
                            height: `${Math.max((count / maxConversations) * 80, count > 0 ? 4 : 0)}px`, 
                            bgcolor: '#617BFF',
                            borderRadius: '3px 3px 0 0',
                            opacity: 0.7 + (i * 0.04),
                          }} 
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      {dayLabels.map((d, i) => (
                        <Typography key={i} sx={{ fontSize: '10px', color: '#9CA3AF' }}>{d}</Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Tokens used (7d) */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 3 }}>
                    Tokens used (7d)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>
                        {dailyAiStatsLoading ? '...' : formatStatNumber(totalTokens7d)}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Last 7 days</Typography>
                    </Box>
                    <Box sx={{ height: 80, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      {tokensPerDay.map((count, i) => (
                        <Box 
                          key={i} 
                          sx={{ 
                            flex: 1, 
                            height: `${Math.max((count / maxTokens) * 80, count > 0 ? 4 : 0)}px`, 
                            bgcolor: '#10B981',
                            borderRadius: '3px 3px 0 0',
                            opacity: 0.7 + (i * 0.04),
                          }} 
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      {dayLabels.map((d, i) => (
                        <Typography key={i} sx={{ fontSize: '10px', color: '#9CA3AF' }}>{d}</Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Requests (7d) */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 3 }}>
                    Requests (7d)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#101828' }}>
                        {dailyAiStatsLoading ? '...' : formatStatNumber(totalRequests7d)}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>Last 7 days</Typography>
                    </Box>
                    <Box sx={{ height: 80, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      {requestsPerDay.map((count, i) => (
                        <Box 
                          key={i} 
                          sx={{ 
                            flex: 1, 
                            height: `${Math.max((count / maxRequests) * 80, count > 0 ? 4 : 0)}px`, 
                            bgcolor: '#8B5CF6',
                            borderRadius: '3px 3px 0 0',
                            opacity: 0.7 + (i * 0.04),
                          }} 
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      {dayLabels.map((d, i) => (
                        <Typography key={i} sx={{ fontSize: '10px', color: '#9CA3AF' }}>{d}</Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
                );
              })()}

              {/* Error Breakdown - HIDDEN */}
              {false && <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 4 }}>
                {/* Error Breakdown */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 3 }}>
                    Error Breakdown
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {[
                      { type: 'Rate Limit', count: 23, color: '#F59E0B' },
                      { type: 'Timeout', count: 12, color: '#EF4444' },
                      { type: 'Auth Error', count: 3, color: '#8B5CF6' },
                      { type: 'Validation', count: 1, color: '#6B7280' },
                    ].map((err, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: err.color }} />
                          <Typography sx={{ fontSize: '12px', color: '#344054' }}>{err.type}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>{err.count}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Cache & Routing */}
                <Box sx={{ 
                  bgcolor: 'white', 
                  borderRadius: '12px', 
                  border: '1px solid #E5E7EB',
                  p: 3,
                }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', mb: 3 }}>
                    Cache & Routing
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '12px', color: '#667085' }}>Cache Hit Rate</Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#10B981' }}>34%</Typography>
                      </Box>
                      <Box sx={{ height: 6, bgcolor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' }}>
                        <Box sx={{ width: '34%', height: '100%', bgcolor: '#10B981', borderRadius: '3px' }} />
                      </Box>
                    </Box>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '12px', color: '#667085' }}>Fallback Routing</Typography>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#F59E0B' }}>2.1%</Typography>
                      </Box>
                      <Box sx={{ height: 6, bgcolor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' }}>
                        <Box sx={{ width: '2.1%', height: '100%', bgcolor: '#F59E0B', borderRadius: '3px' }} />
                      </Box>
                    </Box>
                    <Box sx={{ pt: 1, borderTop: '1px solid #F3F4F6' }}>
                      <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mb: 1 }}>Avg latency comparison</Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#10B981' }}>0.8s</Typography>
                          <Typography sx={{ fontSize: '10px', color: '#667085' }}>Cache hit</Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#667085' }}>1.4s</Typography>
                          <Typography sx={{ fontSize: '10px', color: '#667085' }}>Cache miss</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>}

            </Box>
          </Box>
        ) : (selectedConversation || pendingFirstMessage) ? (
          <>
            {/* Conversation Header - Current Model */}
            <Box sx={{ 
              px: 3, py: 1.5, 
              bgcolor: 'white', 
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                {selectedConversation ? (selectedConversation.title || conversationTitles[selectedConversation.id] || 'New Conversation') : 'New Conversation'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {(() => {
                  const model = availableModels.find(m => m.id === selectedModel);
                  return (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16 }}>
                        {model ? model.providerIcon : getProviderIcon(AiLlmTypes.AiType.OPENAI)}
                      </Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#667085' }}>
                        {model?.name || selectedModel}
                      </Typography>
                    </>
                  );
                })()}
              </Box>
            </Box>
            {/* Messages */}
            <Box sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              py: 4,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#E5E7EB', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { bgcolor: '#D0D5DD' },
              scrollbarWidth: 'thin',
              scrollbarColor: '#E5E7EB transparent',
            }}>
              <Box sx={{ maxWidth: 800, mx: 'auto', px: 3 }}>
                {(loadingMessages || (messages.length === 0 && creatingConversation && !pendingFirstMessage)) ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: '#617BFF' }} />
                  </Box>
                ) : (messages.length === 0 && !pendingFirstMessage && !sending) ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Box sx={{ 
                      width: 64, 
                      height: 64, 
                      borderRadius: '16px', 
                      bgcolor: 'white',
                      border: '1px solid #E5E7EB',
                      display: 'inline-flex',
                      alignItems: 'center', 
                      justifyContent: 'center',
                      mb: 2,
                    }}>
                      <ChatIcon sx={{ fontSize: 28, color: '#9CA3AF' }} />
                    </Box>
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#101828', mb: 0.5 }}>
                      Start the conversation
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                      Send a message to begin chatting with the AI about your repository
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {messages.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          gap: 2,
                          mb: 3,
                          alignItems: 'flex-start',
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: message.role === 'user' ? '#617BFF' : 'white',
                            color: message.role === 'user' ? 'white' : '#617BFF',
                            border: message.role === 'assistant' ? '1px solid #E5E7EB' : 'none',
                            flexShrink: 0,
                          }}
                        >
                          {message.role === 'user' ? <PersonIcon sx={{ fontSize: 18 }} /> : <SmartToyIcon sx={{ fontSize: 18 }} />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ 
                            fontSize: '12px', 
                            fontWeight: 600, 
                            color: '#667085',
                            mb: 0.5,
                          }}>
                            {message.role === 'user' ? 'You' : 'AI Assistant'}
                          </Typography>
                          <Box
                            component="div"
                            className="markdown-body"
                            sx={{
                              fontSize: '14px',
                              lineHeight: '24px',
                              color: '#101828',
                              '& p': { margin: '0 0 0.5em 0' },
                              '& p:last-child': { marginBottom: 0 },
                              '& ul, & ol': { margin: '0.25em 0', paddingLeft: '1.5em' },
                              '& li': { margin: '0.25em 0' },
                              '& strong': { fontWeight: 600 },
                            }}
                          >
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {message.content}
                            </ReactMarkdown>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                    {/* Thinking indicator - shown while waiting for AI response (not when pendingFirstMessage has its own) */}
                    {sending && !pendingFirstMessage && (
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: '#617BFF', border: '1px solid #E5E7EB', flexShrink: 0 }}>
                          <SmartToyIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', mb: 0.5 }}>
                            AI Assistant
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              {[0, 1, 2].map((i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: '#617BFF',
                                    animation: 'chatThinkingPulse 1.4s ease-in-out infinite',
                                    animationDelay: `${i * 0.2}s`,
                                    '@keyframes chatThinkingPulse': {
                                      '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                      '40%': { opacity: 1, transform: 'scale(1)' },
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                            <Typography sx={{ fontSize: '13px', color: '#98A2B3', fontStyle: 'italic' }}>
                              Thinking...
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                {/* Pending first message - shown immediately when user sends from welcome screen */}
                {pendingFirstMessage && messages.length === 0 && (
                  <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#617BFF', color: 'white', flexShrink: 0 }}>
                        <PersonIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', mb: 0.5 }}>
                          You
                        </Typography>
                        <Typography sx={{ fontSize: '14px', lineHeight: '24px', color: '#101828', whiteSpace: 'pre-wrap' }}>
                          {pendingFirstMessage}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Thinking indicator */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'white', color: '#617BFF', border: '1px solid #E5E7EB', flexShrink: 0 }}>
                        <SmartToyIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', mb: 0.5 }}>
                          AI Assistant
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {[0, 1, 2].map((i) => (
                              <Box
                                key={i}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: '#617BFF',
                                  animation: 'chatThinkingPulse 1.4s ease-in-out infinite',
                                  animationDelay: `${i * 0.2}s`,
                                  '@keyframes chatThinkingPulse': {
                                    '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                                    '40%': { opacity: 1, transform: 'scale(1)' },
                                  },
                                }}
                              />
                            ))}
                          </Box>
                          <Typography sx={{ fontSize: '13px', color: '#98A2B3', fontStyle: 'italic' }}>
                            Thinking...
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
                <div ref={messagesEndRef} />
              </Box>
            </Box>

            {/* Input Area with Model Selector */}
            <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #E5E7EB' }}>
              <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                {/* Single Model Dropdown */}
                <Box sx={{ mb: 1.5, position: 'relative', display: 'inline-block' }}>
                  <Box
                    onClick={() => {
                      if (!modelSelectorOpen) {
                        setHoveredProvider(availableProviders[0]?.id || AiLlmTypes.AiType.OPENAI);
                      }
                      setModelSelectorOpen(!modelSelectorOpen);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#617BFF', bgcolor: '#F0F2FF' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16, color: '#617BFF' }}>
                      {(() => {
                        const model = availableModels.find(m => m.id === selectedModel);
                        return model ? model.providerIcon : getProviderIcon(AiLlmTypes.AiType.OPENAI);
                      })()}
                    </Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                      {availableModels.find(m => m.id === selectedModel)?.name || 'Select Model'}
                    </Typography>
                    <KeyboardArrowUpIcon sx={{ fontSize: 16, color: '#667085', transform: modelSelectorOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                  </Box>
                  
                  {/* Two-panel model selector - opens upward */}
                  {modelSelectorOpen && (
                    <Box
                      onMouseLeave={() => {
                        modelSelectorTimeoutRef.current = setTimeout(() => {
                          setModelSelectorOpen(false);
                          setHoveredProvider(null);
                        }, 200);
                      }}
                      onMouseEnter={() => {
                        if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                      }}
                      sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: 0,
                        mb: 0.5,
                        display: 'flex',
                        alignItems: 'flex-end',
                        zIndex: 1300,
                      }}
                    >
                      {/* Providers List */}
                      <Box sx={{
                        bgcolor: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        width: 160,
                        overflow: 'hidden',
                        py: 0.5,
                      }}>
                        {availableProviders.map((provider) => (
                          <Box
                            key={provider.id}
                            onMouseEnter={() => {
                              if (provider.id !== AiLlmTypes.AiType.OTHER) {
                                if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                                setHoveredProvider(provider.id);
                              }
                            }}
                            onClick={() => {
                              if (provider.id === AiLlmTypes.AiType.OTHER) {
                                setModelSearchQuery('');
                                // Set to null to show ALL available models (grouped by provider)
                                setModelSearchProvider(null);
                                setModelSearchModalOpen(true);
                                setModelSelectorOpen(false);
                                setHoveredProvider(null);
                              }
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              px: 2,
                              py: 1.25,
                              cursor: 'pointer',
                              bgcolor: hoveredProvider === provider.id ? '#F0F2FF' : 'transparent',
                              borderLeft: hoveredProvider === provider.id ? '3px solid #617BFF' : '3px solid transparent',
                              '&:hover': { bgcolor: '#F0F2FF' },
                            }}
                          >
                            {provider.icon && <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0 }}>{provider.icon}</Box>}
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828', flex: 1, whiteSpace: 'nowrap' }}>
                              {provider.id === AiLlmTypes.AiType.OTHER ? 'Others' : provider.name}
                            </Typography>
                            {provider.id !== AiLlmTypes.AiType.OTHER && (
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                                <path d="M4.5 2L8.5 6L4.5 10" stroke={hoveredProvider === provider.id ? '#617BFF' : '#9CA3AF'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </Box>
                        ))}
                      </Box>

                      {/* Models Panel - featured + More */}
                      <Box 
                        onMouseEnter={() => {
                          if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                        }}
                        sx={{
                          bgcolor: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          width: 280,
                          ml: 0.5,
                          overflow: 'hidden',
                          py: 0.5,
                          display: hoveredProvider && hoveredProvider !== AiLlmTypes.AiType.OTHER ? 'block' : 'none',
                        }}>
                        {(() => {
                          const providerType = hoveredProvider || AiLlmTypes.AiType.OPENAI;
                          const featured = FEATURED_MODELS[providerType] || [];
                          return featured.map((model) => (
                            <Box
                              key={model.id}
                              onClick={() => {
                                setSelectedModel(model.id);
                                setModelSelectorOpen(false);
                                setHoveredProvider(null);
                              }}
                              sx={{
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                bgcolor: selectedModel === model.id ? '#F0F2FF' : 'transparent',
                                borderLeft: selectedModel === model.id ? '3px solid #617BFF' : '3px solid transparent',
                                '&:hover': { bgcolor: '#F9FAFB' },
                              }}
                            >
                              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828', lineHeight: 1.3 }}>
                                {model.name}
                              </Typography>
                              {'description' in model && (model as any).description && (
                                <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: '#667085', lineHeight: 1.3 }}>
                                  {(model as any).description}
                                </Typography>
                              )}
                            </Box>
                          ));
                        })()}
                        {/* More button */}
                        <Box
                          onClick={() => {
                            setModelSearchQuery('');
                            setModelSearchProvider(hoveredProvider);
                            setModelSearchModalOpen(true);
                            setModelSelectorOpen(false);
                          }}
                          sx={{
                            px: 2,
                            py: 1.25,
                            cursor: 'pointer',
                            borderTop: '1px solid #E5E7EB',
                            '&:hover': { bgcolor: '#F9FAFB' },
                          }}
                        >
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#617BFF' }}>
                            More...
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Input Field */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  p: 1,
                }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Message AI..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sending}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'transparent',
                        '& fieldset': { border: 'none' },
                      },
                      '& .MuiInputBase-input': {
                        fontSize: '14px',
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sending}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: newMessage.trim() && !sending ? '#617BFF' : 'transparent',
                      color: newMessage.trim() && !sending ? 'white' : '#9CA3AF',
                      borderRadius: '8px',
                      alignSelf: 'flex-end',
                      '&:hover': { 
                        bgcolor: newMessage.trim() && !sending ? '#5A6FE8' : 'rgba(0,0,0,0.04)',
                      },
                      '&:disabled': { bgcolor: 'transparent' },
                    }}
                  >
                    {sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#9CA3AF', mt: 1, textAlign: 'center' }}>
                  Press Enter to send, Shift+Enter for new line
                </Typography>
              </Box>
            </Box>
          </>
        ) : (
          /* New Chat Screen - ChatGPT Style */
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
          }}>
            {/* Center Content */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center',
              px: 4,
              pb: 8,
            }}>
              {/* Main Welcome Text */}
              <Typography sx={{ 
                fontSize: '36px', 
                fontWeight: 700, 
                color: '#101828', 
                mb: 2,
                textAlign: 'center',
              }}>
                What can I help you with?
              </Typography>
              <Typography sx={{ fontSize: '15px', color: '#667085', textAlign: 'center', maxWidth: 500, mb: 4 }}>
                Ask anything about your repository. I have full context of your data.
              </Typography>

            {/* Input Area */}
            <Box sx={{ 
              width: '100%', 
              maxWidth: 680,
              bgcolor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: '16px',
              p: 1.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                      e.preventDefault();
                      handleCreateConversationWithMessage(newMessage.trim());
                    }
                  }}
                  disabled={creatingConversation || sending}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'transparent',
                      '& fieldset': { border: 'none' },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '15px',
                    },
                  }}
                />
                <IconButton
                  onClick={() => {
                    if (newMessage.trim()) {
                      handleCreateConversationWithMessage(newMessage.trim());
                    }
                  }}
                  disabled={!newMessage.trim() || creatingConversation || sending}
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: newMessage.trim() && !creatingConversation && !sending ? '#617BFF' : '#E5E7EB',
                    color: newMessage.trim() && !creatingConversation && !sending ? 'white' : '#9CA3AF',
                    borderRadius: '12px',
                    flexShrink: 0,
                    '&:hover': { 
                      bgcolor: newMessage.trim() && !creatingConversation && !sending ? '#5A6FE8' : '#E5E7EB',
                    },
                    '&:disabled': { bgcolor: '#E5E7EB' },
                  }}
                >
                  {creatingConversation || sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Box>
              
              {/* Model Selector - Provider button with dropdown */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F3F4F6', display: 'flex', justifyContent: 'flex-start', position: 'relative' }}>
                <Box
                  onClick={() => setModelSelectorOpen(!modelSelectorOpen)}
                  onMouseEnter={() => {
                    if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                    setModelSelectorOpen(true);
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    bgcolor: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: '#617BFF', bgcolor: '#F0F2FF' },
                  }}
                >

                  {(() => {
                    const model = availableModels.find(m => m.id === selectedModel);
                    return (
                      <>
                        <span style={{ display: 'flex', alignItems: 'center', color: '#617BFF' }}>
                          {model ? model.providerIcon : getProviderIcon(AiLlmTypes.AiType.OPENAI)}
                        </span>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828' }}>
                          {model?.name || 'Select Model'}
                        </Typography>
                      </>
                    );
                  })()}
                  <KeyboardArrowDownIcon sx={{ fontSize: 16, color: '#667085', transform: modelSelectorOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </Box>
                
                {/* Provider → Models Dropdown */}
                {modelSelectorOpen && (
                  <Box
                    onMouseEnter={() => {
                      if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                    }}
                    onMouseLeave={() => {
                      modelSelectorTimeoutRef.current = setTimeout(() => {
                        setModelSelectorOpen(false);
                        setHoveredProvider(null);
                      }, 150);
                    }}
                    sx={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      mb: 0.5,
                      display: 'flex',
                      alignItems: 'flex-end',
                      zIndex: 1300,
                    }}
                  >
                    {/* Providers List */}
                    <Box sx={{
                      bgcolor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                      width: 160,
                      overflow: 'hidden',
                      py: 0.5,
                    }}>
                      {availableProviders.map((provider) => (
                        <Box
                          key={provider.id}
                          onMouseEnter={() => {
                            if (provider.id !== AiLlmTypes.AiType.OTHER) {
                              if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                              setHoveredProvider(provider.id);
                            }
                          }}
                          onClick={() => {
                            // "Others" → open search popup showing ALL models
                            if (provider.id === AiLlmTypes.AiType.OTHER) {
                              setModelSearchQuery('');
                              // Set to null to show ALL available models (grouped by provider)
                              setModelSearchProvider(null);
                              setModelSearchModalOpen(true);
                              setModelSelectorOpen(false);
                              setHoveredProvider(null);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            cursor: 'pointer',
                            bgcolor: hoveredProvider === provider.id ? '#F0F2FF' : 'transparent',
                            borderLeft: hoveredProvider === provider.id ? '3px solid #617BFF' : '3px solid transparent',
                            '&:hover': { bgcolor: '#F0F2FF' },
                          }}
                        >
                          {provider.icon && <Box sx={{ display: 'flex', alignItems: 'center', width: 16, height: 16, flexShrink: 0 }}>{provider.icon}</Box>}
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#101828', flex: 1, whiteSpace: 'nowrap' }}>
                            {provider.id === AiLlmTypes.AiType.OTHER ? 'Others' : provider.name}
                          </Typography>
                          {provider.id !== AiLlmTypes.AiType.OTHER && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                              <path d="M4.5 2L8.5 6L4.5 10" stroke={hoveredProvider === provider.id ? '#617BFF' : '#9CA3AF'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </Box>
                      ))}
                    </Box>
                    
                    {/* Models Panel - 4 featured + More */}
                    <Box 
                      onMouseEnter={() => {
                        if (modelSelectorTimeoutRef.current) clearTimeout(modelSelectorTimeoutRef.current);
                      }}
                      sx={{
                        bgcolor: 'white',
                        border: '1px solid #E5E7EB',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                        width: 280,
                        ml: 0.5,
                        overflow: 'hidden',
                        py: 0.5,
                        display: hoveredProvider && hoveredProvider !== AiLlmTypes.AiType.OTHER ? 'block' : 'none',
                      }}>
                      {(() => {
                        const providerType = hoveredProvider || AiLlmTypes.AiType.OPENAI;
                        const featured = FEATURED_MODELS[providerType] || [];
                        return featured.map((model) => (
                          <Box
                            key={model.id}
                            onClick={() => {
                              setSelectedModel(model.id);
                              setModelSelectorOpen(false);
                              setHoveredProvider(null);
                            }}
                            sx={{
                              px: 2,
                              py: 1,
                              cursor: 'pointer',
                              bgcolor: selectedModel === model.id ? '#F0F2FF' : 'transparent',
                              borderLeft: selectedModel === model.id ? '3px solid #617BFF' : '3px solid transparent',
                              '&:hover': { bgcolor: '#F9FAFB' },
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828', lineHeight: 1.3 }}>
                              {model.name}
                            </Typography>
                            {'description' in model && (model as any).description && (
                              <Typography sx={{ fontSize: '11px', fontStyle: 'italic', color: '#667085', lineHeight: 1.3 }}>
                                {(model as any).description}
                              </Typography>
                            )}
                          </Box>
                        ));
                      })()}
                      {/* More button */}
                      <Box
                        onClick={() => {
                          setModelSearchQuery('');
                          setModelSearchProvider(hoveredProvider);
                          setModelSearchModalOpen(true);
                          setModelSelectorOpen(false);
                        }}
                        sx={{
                          px: 2,
                          py: 1.25,
                          cursor: 'pointer',
                          borderTop: '1px solid #E5E7EB',
                          '&:hover': { bgcolor: '#F9FAFB' },
                        }}
                      >
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#617BFF' }}>
                          More...
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>

            </Box>

            {/* Quick Suggestions */}
            <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', maxWidth: 680 }}>
              {[
                'How do I use buckets effectively?',
                'How to attach an AI resource?',
                'How are policy violations detected?',
                'What are best practices for data storage?',
              ].map((suggestion, idx) => (
                <Box
                  key={idx}
                  onClick={() => setNewMessage(suggestion)}
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '20px',
                    fontSize: '13px',
                    color: '#667085',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: '#617BFF', color: '#617BFF', bgcolor: '#F0F2FF' },
                  }}
                >
                  {suggestion}
                </Box>
              ))}
            </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Chat Environment Selection Modal */}
      <Modal
        open={environmentModalOpen}
        onClose={() => {
          // Always allow closing - environment selection is optional
          setEnvironmentModalOpen(false);
        }}
        closeAfterTransition
      >
        <Fade in={environmentModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            maxWidth: '90vw',
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            outline: 'none',
            overflow: 'hidden',
          }}>
            {/* Modal Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}>
              <Box sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: '12px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <ChatIcon sx={{ fontSize: 24, color: 'white' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#101828' }}>
                  Select Chat Environment
                </Typography>
                <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                  Choose your chat environment to start a conversation
                </Typography>
              </Box>
            </Box>

            {/* Modal Content */}
            <Box sx={{ p: 3 }}>
              {/* Environment Selection - Unified list with search */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#344054', mb: 1.5 }}>
                  Environment
                </Typography>
                
                {/* Search Bar */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  mb: 2,
                }}>
                  <SearchIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                  <input
                    type="text"
                    placeholder="Search workspaces and repositories..."
                    value={environmentSearchQuery}
                    onChange={(e) => setEnvironmentSearchQuery(e.target.value)}
                    style={{
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      flex: 1,
                      fontSize: '14px',
                      color: '#101828',
                    }}
                  />
                  {environmentSearchQuery && (
                    <IconButton 
                      size="small" 
                      onClick={() => setEnvironmentSearchQuery('')}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                    </IconButton>
                  )}
                </Box>

                {/* Unified Workspace + Repository List */}
                <Box sx={{ 
                  maxHeight: 320, 
                  overflowY: 'auto',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                }}>
                  {allReposGrouped.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#667085' }}>
                        No repositories available
                      </Typography>
                    </Box>
                  ) : (
                    allReposGrouped
                      .filter(group => {
                        if (!environmentSearchQuery.trim()) return true;
                        const query = environmentSearchQuery.toLowerCase();
                        // Match workspace name or any repo name in the group
                        return group.workspace.name.toLowerCase().includes(query) ||
                          group.repos.some((repo: any) => repo.name.toLowerCase().includes(query));
                      })
                      .map((group, groupIndex) => {
                        // Filter repos within the group
                        const filteredRepos = environmentSearchQuery.trim()
                          ? group.repos.filter((repo: any) => 
                              repo.name.toLowerCase().includes(environmentSearchQuery.toLowerCase()) ||
                              group.workspace.name.toLowerCase().includes(environmentSearchQuery.toLowerCase())
                            )
                          : group.repos;
                        
                        if (filteredRepos.length === 0) return null;
                        
                        return (
                          <Box key={group.workspace.id}>
                            {/* Workspace Header */}
                            <Box sx={{ 
                              px: 2, 
                              py: 1.25, 
                              bgcolor: '#F9FAFB',
                              borderBottom: '1px solid #E5E7EB',
                              borderTop: groupIndex > 0 ? '1px solid #E5E7EB' : 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}>
                              <WorkspacesIcon sx={{ fontSize: 16, color: '#667085' }} />
                              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {group.workspace.name}
                              </Typography>
                            </Box>
                            
                            {/* Repos under this workspace */}
                            {filteredRepos.map((repo: any) => (
                              <Box
                                key={repo.id}
                                onClick={() => {
                                  // Mark that we're explicitly selecting a repo, skip the reset
                                  skipRepoResetRef.current = true;
                                  setEnvironmentWorkspaceId(group.workspace.id);
                                  setEnvironmentRepoId(repo.id);
                                  loadRepos(group.workspace.id);
                                }}
                                sx={{
                                  px: 2,
                                  py: 1.5,
                                  pl: 4,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  cursor: 'pointer',
                                  bgcolor: environmentRepoId === repo.id ? '#F0F2FF' : 'transparent',
                                  borderLeft: environmentRepoId === repo.id ? '3px solid #617BFF' : '3px solid transparent',
                                  '&:hover': { bgcolor: environmentRepoId === repo.id ? '#F0F2FF' : '#F9FAFB' },
                                  transition: 'all 0.15s',
                                }}
                              >
                                <FolderIcon sx={{ fontSize: 18, color: environmentRepoId === repo.id ? '#617BFF' : '#667085' }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#101828' }}>
                                    {repo.name}
                                  </Typography>
                                  {/* Provider icons */}
                                  {repo.aiApiKeys && repo.aiApiKeys.length > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {repo.aiApiKeys.slice(0, 4).map((key: any, idx: number) => {
                                        const icon = getProviderIconFromAiType(key.aiType);
                                        return icon ? (
                                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', opacity: 0.8 }}>
                                            {icon}
                                          </Box>
                                        ) : null;
                                      })}
                                      {repo.aiApiKeys.length > 4 && (
                                        <Typography sx={{ fontSize: '10px', color: '#667085', ml: 0.5 }}>
                                          +{repo.aiApiKeys.length - 4}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                                {/* Currently used label */}
                                {savedEnv?.repoId === repo.id && environmentRepoId !== repo.id && (
                                  <Typography sx={{ fontSize: '11px', color: '#667085', fontStyle: 'italic', mr: 1 }}>
                                    currently used
                                  </Typography>
                                )}
                                {environmentRepoId === repo.id && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '11px', color: '#617BFF', fontWeight: 500 }}>
                                      currently used
                                    </Typography>
                                    <Box sx={{ 
                                      width: 20, 
                                      height: 20, 
                                      borderRadius: '50%', 
                                      bgcolor: '#617BFF', 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center' 
                                    }}>
                                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        );
                      })
                  )}
                </Box>
              </Box>
            </Box>

            {/* Modal Footer */}
            <Box sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              {isEnvironmentSelected && (
                <Button
                  variant="outlined"
                  onClick={() => setEnvironmentModalOpen(false)}
                  sx={{
                    borderColor: '#E5E7EB',
                    color: '#667085',
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    px: 3,
                    '&:hover': { borderColor: '#617BFF', color: '#617BFF' },
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={() => {
                  if (isEnvironmentSelected) {
                    setEnvironmentModalOpen(false);
                    // Also set the selectedRepoId for creating conversations
                    setSelectedRepoId(environmentRepoId);
                    // Save environment selection to localStorage for Header sync
                    const wsName = workspaces.find((w: any) => w.id === environmentWorkspaceId)?.name || '';
                    const repoName = repos.find((r: any) => r.id === environmentRepoId)?.name || '';
                    localStorage.setItem('chatEnvironment', JSON.stringify({
                      workspaceId: environmentWorkspaceId,
                      workspaceName: wsName,
                      repoId: environmentRepoId,
                      repoName: repoName,
                    }));
                    window.dispatchEvent(new CustomEvent('chatEnvironmentChanged'));
                  }
                }}
                disabled={!isEnvironmentSelected}
                sx={{
                  bgcolor: '#617BFF',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { bgcolor: '#5A6FE8' },
                  '&:disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' },
                }}
              >
                Continue
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* No Resources Warning Modal - For new users */}
      <Modal
        open={noResourcesModalOpen}
        onClose={() => {
          // Don't allow closing by clicking outside - user must take action
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }
          }
        }}
      >
        <Fade in={noResourcesModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            maxWidth: '90vw',
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            outline: 'none',
            overflow: 'hidden',
          }}>
            {/* Modal Header */}
            <Box sx={{ 
              p: 3, 
              pb: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
              {/* Warning Icon */}
              <Box sx={{ 
                width: 64, 
                height: 64, 
                borderRadius: '16px', 
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </Box>
              
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#101828', mb: 1 }}>
                {hasNoWorkspaces ? 'No Workspace Available' : hasNoRepos ? 'No Repository Available' : 'No AI Configuration'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#667085', lineHeight: 1.6, mb: 3 }}>
                {hasNoWorkspaces 
                  ? 'Private Chat requires a workspace with at least one repository and AI configuration. Create a workspace first to get started.'
                  : hasNoRepos
                    ? 'Private Chat requires at least one repository. Create a repository in your workspace to enable AI conversations.'
                    : 'Private Chat requires at least one AI provider configuration. Add an AI provider (OpenAI, Anthropic, etc.) in Flashgate to start chatting.'
                }
              </Typography>
            </Box>

            {/* Modal Content - Steps */}
            <Box sx={{ px: 3, pb: 2 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#717680', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                Requirements for Private Chat
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Workspace step */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  p: 1.5, 
                  bgcolor: hasNoWorkspaces ? '#F9FAFB' : '#F0FDF4', 
                  borderRadius: '10px',
                  border: hasNoWorkspaces ? 'none' : '1px solid #BBF7D0'
                }}>
                  <Box sx={{ 
                    width: 28, height: 28, borderRadius: '50%', 
                    bgcolor: hasNoWorkspaces ? '#617BFF' : '#10B981', 
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                    fontSize: '13px', fontWeight: 600, flexShrink: 0 
                  }}>
                    {hasNoWorkspaces ? '1' : '✓'}
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: hasNoWorkspaces ? '#344054' : '#166534' }}>
                    Create a workspace
                  </Typography>
                </Box>
                
                {/* Repository step */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  p: 1.5, 
                  bgcolor: hasNoWorkspaces ? '#F9FAFB' : (hasNoRepos ? '#F9FAFB' : '#F0FDF4'), 
                  borderRadius: '10px',
                  border: (!hasNoWorkspaces && !hasNoRepos) ? '1px solid #BBF7D0' : 'none'
                }}>
                  <Box sx={{ 
                    width: 28, height: 28, borderRadius: '50%', 
                    bgcolor: hasNoWorkspaces ? '#E5E7EB' : (hasNoRepos ? '#617BFF' : '#10B981'), 
                    color: hasNoWorkspaces ? '#667085' : 'white', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                    fontSize: '13px', fontWeight: 600, flexShrink: 0 
                  }}>
                    {hasNoWorkspaces ? '2' : (hasNoRepos ? '2' : '✓')}
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: hasNoWorkspaces ? '#667085' : (hasNoRepos ? '#344054' : '#166534') }}>
                    Create a repository
                  </Typography>
                </Box>
                
                {/* AI Configuration step */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  p: 1.5, 
                  bgcolor: (hasNoWorkspaces || hasNoRepos) ? '#F9FAFB' : (hasNoAiConfigs ? '#F9FAFB' : '#F0FDF4'), 
                  borderRadius: '10px',
                  border: (!hasNoWorkspaces && !hasNoRepos && !hasNoAiConfigs) ? '1px solid #BBF7D0' : 'none'
                }}>
                  <Box sx={{ 
                    width: 28, height: 28, borderRadius: '50%', 
                    bgcolor: (hasNoWorkspaces || hasNoRepos) ? '#E5E7EB' : (hasNoAiConfigs ? '#617BFF' : '#10B981'), 
                    color: (hasNoWorkspaces || hasNoRepos) ? '#667085' : 'white', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                    fontSize: '13px', fontWeight: 600, flexShrink: 0 
                  }}>
                    {(hasNoWorkspaces || hasNoRepos) ? '3' : (hasNoAiConfigs ? '3' : '✓')}
                  </Box>
                  <Typography sx={{ fontSize: '13px', color: (hasNoWorkspaces || hasNoRepos) ? '#667085' : (hasNoAiConfigs ? '#344054' : '#166534') }}>
                    Configure an AI provider
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Modal Footer */}
            <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setNoResourcesModalOpen(false);
                  navigate('/hub');
                }}
                sx={{
                  borderColor: '#E5E7EB',
                  color: '#667085',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  '&:hover': { borderColor: '#617BFF', color: '#617BFF' },
                }}
              >
                Back to Main
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setNoResourcesModalOpen(false);
                  navigate('/workspace/home');
                }}
                startIcon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                }
                sx={{
                  bgcolor: '#617BFF',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { bgcolor: '#5A6FE8' },
                }}
              >
                Go to Gateway
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Suggest Workspace Modal - When current workspace has no repos but others do */}
      <Modal
        open={suggestWorkspaceModalOpen}
        onClose={() => { /* Don't allow closing by clicking outside */ }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }
          }
        }}
      >
        <Fade in={suggestWorkspaceModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            maxWidth: '90vw',
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            outline: 'none',
            overflow: 'hidden',
          }}>
            {/* Modal Header */}
            <Box sx={{ 
              p: 3, 
              pb: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}>
              {/* Info Icon */}
              <Box sx={{ 
                width: 64, 
                height: 64, 
                borderRadius: '16px', 
                background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 2,
              }}>
                <WorkspacesIcon sx={{ fontSize: 32, color: '#617BFF' }} />
              </Box>
              
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#101828', mb: 1 }}>
                No Repository in This Workspace
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#667085', lineHeight: 1.6, mb: 3 }}>
                The selected workspace doesn't have any repositories yet. You can switch to another workspace that has repositories, or create a new repository.
              </Typography>
            </Box>

            {/* Workspaces with repos list */}
            <Box sx={{ px: 3, pb: 2 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#717680', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                Workspaces with Repositories
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 200, overflowY: 'auto' }}>
                {workspacesWithRepos.map((ws: any) => (
                  <Box 
                    key={ws.id}
                    onClick={() => {
                      setSuggestWorkspaceModalOpen(false);
                      setEnvironmentWorkspaceId(ws.id);
                      loadRepos(ws.id);
                    }}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      p: 1.5, 
                      bgcolor: '#F9FAFB', 
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: '#EEF2FF',
                        borderColor: '#617BFF',
                      }
                    }}
                  >
                    <Box sx={{ 
                      width: 36, height: 36, borderRadius: '10px', 
                      background: 'linear-gradient(135deg, #617BFF 0%, #8B5CF6 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      flexShrink: 0 
                    }}>
                      <WorkspacesIcon sx={{ fontSize: 18, color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#344054' }}>
                        {ws.name}
                      </Typography>
                    </Box>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Modal Footer */}
            <Box sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setSuggestWorkspaceModalOpen(false);
                  navigate('/hub');
                }}
                sx={{
                  borderColor: '#E5E7EB',
                  color: '#667085',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  px: 3,
                  '&:hover': { borderColor: '#617BFF', color: '#617BFF' },
                }}
              >
                Back to Main
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSuggestWorkspaceModalOpen(false);
                  navigate('/cloud-storage/overview');
                }}
                startIcon={<AddIcon />}
                sx={{
                  bgcolor: '#617BFF',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': { bgcolor: '#5A6FE8' },
                }}
              >
                Create Repository
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
      
      {/* Private Chat Tutorial */}
      <PrivateChatTutorial
        open={showChatTutorial}
        onClose={() => setShowChatTutorial(false)}
        onComplete={handleChatTutorialComplete}
      />

      {/* Model Search Modal */}
      <Modal
        open={modelSearchModalOpen}
        onClose={() => setModelSearchModalOpen(false)}
        closeAfterTransition
      >
        <Fade in={modelSearchModalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 520,
            maxWidth: '90vw',
            maxHeight: '80vh',
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            outline: 'none',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Modal Header */}
            <Box sx={{ 
              p: 3, 
              borderBottom: '1px solid #E5E7EB',
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#101828' }}>
                  Select AI Model
                </Typography>
                <IconButton size="small" onClick={() => setModelSearchModalOpen(false)}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>
              {/* Search Input */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search models..."
                value={modelSearchQuery}
                onChange={(e) => setModelSearchQuery(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ fontSize: 18, color: '#9CA3AF', mr: 1 }} />
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#617BFF' },
                    '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                  },
                }}
              />
            </Box>

            {/* Models List - filtered by provider if opened via 'More' */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {AI_PROVIDERS
                .filter(provider => !modelSearchProvider || provider.id === modelSearchProvider)
                .map((provider) => {
                const providerModels = availableModels.filter(m => m.aiType === provider.id);
                const filteredModels = providerModels.filter(m =>
                  !modelSearchQuery ||
                  m.name.toLowerCase().includes(modelSearchQuery.toLowerCase()) ||
                  provider.name.toLowerCase().includes(modelSearchQuery.toLowerCase())
                );
                if (filteredModels.length === 0) return null;

                return (
                  <Box key={provider.id} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, px: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: 18, height: 18 }}>
                        {provider.icon}
                      </Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#667085', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {provider.name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {filteredModels.map((model) => (
                        <Box
                          key={model.id}
                          onClick={() => {
                            setSelectedModel(model.id);
                            setModelSearchModalOpen(false);
                          }}
                          sx={{
                            p: 1.5,
                            borderRadius: '10px',
                            cursor: 'pointer',
                            bgcolor: selectedModel === model.id ? '#F0F2FF' : 'transparent',
                            border: selectedModel === model.id ? '1px solid #617BFF' : '1px solid transparent',
                            '&:hover': { bgcolor: '#F9FAFB' },
                          }}
                        >
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                            {model.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
              {/* No results message for search */}
              {modelSearchQuery && availableModels.length > 0 && !availableModels.some(m =>
                m.name.toLowerCase().includes(modelSearchQuery.toLowerCase())
              ) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ fontSize: '14px', color: '#667085' }}>
                    No models found matching "{modelSearchQuery}"
                  </Typography>
                </Box>
              )}
              {/* No models message when list is empty */}
              {availableModels.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <Typography sx={{ fontSize: '14px', color: '#667085', mb: 1 }}>
                    No AI models available
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
                    Configure AI LLMs in your repository to access models
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Sidebar collapse button - fixed position */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ease-in-out hover:bg-[#EEF2FF] shadow-sm"
        style={{
          position: 'fixed',
          left: sidebarCollapsed ? '16px' : '286px',
          top: sidebarCollapsed ? '86px' : '78px',
          backgroundColor: 'white',
          border: '1px solid #617BFF',
          zIndex: 40,
          cursor: 'pointer'
        }}
        title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#617BFF"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: sidebarCollapsed ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.3s ease'
          }}
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      </Box>
    </Box>
  );
};

export default Chat;
